import type {
  Permission,
  RewardMechanismConfig,
  SubscriptionBillingCycle,
  SubscriptionPaymentMethod,
  SubscriptionPlanCode,
  TenantRole,
} from "@loyalty/shared";

export type Portal = "customer" | "tenant" | "super-admin";
export type SessionType = "customer" | "tenant_staff" | "super_admin";

export type TenantRewardLifecycleStatus =
  | "draft"
  | "pending_approval"
  | "approved"
  | "scheduled"
  | "active"
  | "paused"
  | "expired"
  | "archived"
  | "rejected"
  | "inactive";

export interface TenantRewardEligibilityRule {
  dimension: string;
  field: string;
  operator: string;
  value: unknown;
}

export interface Reward {
  publicId: string;
  tenantPublicId: string;
  tenantName: string;
  tenantSlug: string;
  title: string;
  description: string;
  category: string;
  pointsCost: number;
  monetaryValue: string | null;
  expiresAt: string | null;
  imageUrl: string | null;
  available: boolean;
  featured: boolean;
  status: TenantRewardLifecycleStatus;
  inventoryTotal: number;
  inventoryRemaining: number;
  redeemedCount: number;
  internalName: string;
  rewardType: string;
  mechanismConfig?: RewardMechanismConfig | Record<string, never>;
  mechanismLiabilityMinor?: number;
  mechanismTerms?: string[];
  objective: string;
  rewardValueMinor: number | null;
  minimumSpendMinor: number;
  maximumDiscountMinor: number | null;
  currencyCode: string;
  expirationMode: string;
  expirationDaysAfterIssue: number | null;
  eligibleTierCodes: string[];
  eligibleSegmentIds: string[];
  eligibleLocationPublicIds: string[];
  eligibleLocationNames: string[];
  channelScope: string[];
  perMemberLimit: number;
  globalIssueLimit: number | null;
  globalRedemptionLimit: number | null;
  budgetMinor: number | null;
  stackingMode: string;
  priority: number;
  requiresApproval: boolean;
  termsText: string;
  eligibilityRules: TenantRewardEligibilityRule[];
  lifecycleStatus: TenantRewardLifecycleStatus;
  lifecycleVersion: number;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface InventoryItem {
  publicId: string;
  code: string;
  name: string;
  brand: string | null;
  category: string;
  priceMinor: number | null;
  stockQuantity: number | null;
  imageUrl: string | null;
  status: "active" | "inactive";
  createdAt: string;
  updatedAt: string;
}

export interface SessionUser {
  publicId: string;
  name: string;
  email: string;
  sessionType: SessionType;
  tenantName: string | null;
  tenantSlug: string | null;
  pointsBalance: number;
  roles: TenantRole[];
  permissions: Permission[];
  allowedLocationIds: string[];
}

export interface Tenant {
  publicId: string;
  name: string;
  slug: string;
  status: "trial" | "active" | "suspended" | "cancelled" | "archived";
  contactEmail: string;
  legalName: string | null;
  businessType: string;
  businessPhone: string | null;
  website: string | null;
  defaultCurrency: string;
  defaultTimezone: string;
  country: string;
  address: string | null;
  city: string | null;
  stateProvince: string | null;
  postalCode: string | null;
  logoUrl: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  plan: { id: string; name: string } | null;
  createdAt: string | null;
  locationCount: number;
  staffCount: number;
  rewardCount: number;
  businessPanNumber: string | null;
  applicationStatus: "PENDING_REVIEW" | "APPROVED" | "REJECTED";
  applicationSubmittedAt: string | null;
  applicationReviewedAt: string | null;
  applicationReviewNote: string | null;
  ownerEmailVerified: boolean;
  setupStatus: "SETUP_REQUIRED" | "LIVE";
  setupCompletedAt: string | null;
}

export interface Redemption {
  publicId: string;
  rewardTitle: string;
  tenantName: string;
  pointsSpent: number;
  code: string | null;
  qrPayload: string | null;
  manualCode: string | null;
  qrExpiresAt: string | null;
  status: string;
  createdAt: string | null;
  fulfilledAt: string | null;
}

export interface CustomerAccount {
  tenant: {
    publicId: string;
    slug: string;
    businessName: string;
    logoUrl: string | null;
  };
  program: {
    publicId: string;
    name: string;
  };
  pointsBalance: number;
  redemption: {
    rulePublicId: string;
    pointsPerUnit: number;
    valuePerUnitMinor: number;
    minimumRedemptionPoints: number;
    maximumRedemptionPointsPerTransaction: number | null;
    redeemablePoints: number;
    redeemableValueMinor: number;
  } | null;
}

export interface CustomerNotification {
  publicId: string;
  kind: "REWARD_EXPIRING" | "POINTS_EXPIRING";
  title: string;
  message: string;
  actionPath: string | null;
  expiresAt: string;
  payload: unknown;
  createdAt: string;
  tenant: {
    publicId: string;
    name: string;
  };
}

export interface TierBenefit {
  publicId: string;
  title: string;
  description: string;
}

export interface TierStatus {
  enrolled: boolean;
  tier: {
    publicId: string;
    code: string;
    name: string;
    rank: number;
    colorHex: string;
    accentHex: string;
    description: string;
    minimumTierPoints: number;
    benefits?: TierBenefit[];
  } | null;
  activeTierPoints: number;
  lifetimeTierPoints: number;
  pointsToNextTier: number | null;
  nextTier: {
    publicId: string;
    code: string;
    name: string;
    minimumTierPoints: number;
  } | null;
  tierValidFrom: string | null;
  tierValidUntil: string | null;
  nextAssessmentAt: string | null;
}

export interface MembershipQr {
  displayName: string;
  payload: string;
  expiresAt: string;
}

export interface TierActivity {
  publicId: string;
  tenantName: string | null;
  locationName: string | null;
  eligibleSpendMinor: number;
  tierPointsDelta: number;
  entryType: string;
  qualificationStatus: string;
  riskStatus: string;
  effectiveAt: string;
}

export interface CheckoutLocation {
  publicId: string;
  name: string;
  code: string;
  city: string | null;
  timezone: string;
  tenantName?: string;
  tenantSlug?: string;
}

export interface CheckoutTransaction {
  id: string;
  publicId: string;
  tenantSlug: string;
  tenantName: string;
  locationPublicId: string;
  locationName: string;
  customerPublicId: string;
  customerName: string;
  customerEmail: string;
  sourceType: string;
  grossAmountMinor: number;
  qualifyingAmountMinor: number;
  rewardPointsRedeemed: number;
  redemptionValueMinor: number;
  netPayableAmountMinor: number;
  currencyCode: string;
  paymentType: string | null;
  status: string;
  riskScore: number;
  rewardPointsAwarded: number;
  tierPointsAwarded: number;
  occurredAt: string;
  createdAt: string;
  receiptPublicId: string | null;
  receiptNumber: string | null;
}

export interface ReceiptSubmission {
  publicId: string;
  tenantSlug: string;
  tenantName: string;
  locationPublicId: string | null;
  locationName: string | null;
  customerName: string;
  grossAmountMinor: number;
  qualifyingAmountMinor: number;
  currencyCode: string;
  merchantReference: string | null;
  status: string;
  submittedAt: string;
  rejectionReason: string | null;
  transactionPublicId: string | null;
}

export interface CustomerMembership extends CustomerAccount {
  membershipPublicId: string;
  tenant: CustomerAccount["tenant"] & { primaryColor: string };
  joinedAt: string;
  lastVisitAt: string | null;
  expiringPoints: number;
  nextPointsExpiry: string | null;
  availableRewards: number;
  preferences: { inApp: boolean; email: boolean; push: boolean; sms: boolean };
}

export interface CustomerActivityItem {
  publicId: string;
  kind: string;
  tenantPublicId: string | null;
  tenantSlug: string | null;
  tenantName: string | null;
  title: string;
  detail: string;
  rewardPointsDelta: number;
  tierPointsDelta: number;
  amountMinor: number | null;
  currencyCode: string | null;
  status: string;
  occurredAt: string;
}

export interface CustomerEntitlement {
  publicId: string;
  status: string;
  issuedAt: string;
  startsAt: string | null;
  expiresAt: string | null;
  valueMinor: number;
  reward: {
    publicId: string;
    name: string;
    description: string;
    imageUrl: string | null;
    rewardType: string;
    pointsCost: number;
    termsText: string;
  };
  tenant: { publicId: string; slug: string; businessName: string; logoUrl: string | null };
}

export interface CustomerPreferences {
  locale: string;
  timezone: string;
  serviceNotifications: boolean;
  marketingEmail: boolean;
  marketingPush: boolean;
  marketingSms: boolean;
}

export interface CustomerProfileDetail {
  publicId: string;
  email: string;
  phone: string | null;
  firstName: string;
  lastName: string;
  birthday: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  district: string | null;
  province: string | null;
  country: string | null;
  postalCode: string | null;
  profileImageUrl: string | null;
  createdAt: string;
}

export type CustomerProfileUpdateInput = Omit<CustomerProfileDetail, "publicId" | "createdAt">;

export interface BusinessOwnerAccountDetail {
  publicId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  profileImageUrl: string | null;
  status: string;
  emailVerified: boolean;
  createdAt: string;
  updatedAt: string;
  businessName: string;
  businessLogoUrl: string | null;
}

export interface BusinessSetupStatus {
  status: "SETUP_REQUIRED" | "LIVE";
  isLive: boolean;
  earningRuleReady: boolean;
  redemptionRuleReady: boolean;
  quickOfferReady: boolean;
  nextStep: "earning_rule" | "redemption_rule" | "quick_offer" | null;
  completedAt: string | null;
}

export interface BusinessSubscription {
  planCode: "BASIC" | "PRO" | "MASTER";
  billingCycle: "MONTHLY" | "ANNUAL";
  status: "PENDING_APPROVAL" | "ACTIVE" | "TRIALING" | "PAST_DUE" | "CANCELED";
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  currentPeriodStartedAt: string | null;
  currentPeriodEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
  accessPlanCode: "BASIC" | "PRO" | "MASTER";
  trialExpired: boolean;
  selectedPlan: {
    code: "BASIC" | "PRO" | "MASTER";
    name: string;
    tagline: string;
    monthlyPrice: number;
    annualPrice: number;
    trialDays: number;
    features: Array<
      | "loyalty_rules"
      | "redemption"
      | "punch_cards"
      | "inventory"
      | "quick_offers"
      | "basic_analytics"
      | "mero_credits"
      | "segments"
      | "campaigns"
      | "advanced_analytics"
      | "automations"
      | "data_exports"
      | "priority_support"
    >;
    displayFeatures: string[];
    limits: Record<string, number | null>;
  };
  accessPlan: BusinessSubscription["selectedPlan"];
  usage: Record<string, number>;
}

export interface BusinessSubscriptionChangeResult {
  subscription: BusinessSubscription;
  payment: {
    provider: "DUMMY";
    reference: string;
    status: "SUCCEEDED" | "NOT_REQUIRED";
    amountMinor: number;
    currencyCode: "NPR";
  };
}

export interface CustomerProfileUpdateResult {
  profile: CustomerProfileDetail;
  emailChanged: boolean;
  verificationRequired: boolean;
  delivery: string | null;
}

export interface CustomerHomeData {
  profile: CustomerProfileDetail;
  tierSummary: TierStatus;
  urgentItems: CustomerNotification[];
  businessWalletSummaries: CustomerMembership[];
  recommendedOffers: Reward[];
  recentActivity: CustomerActivityItem[];
  pendingActions: Array<{
    publicId: string;
    kind: string;
    tenantName: string;
    tenantSlug: string;
    amountMinor: number;
    currencyCode: string;
    status: string;
    occurredAt: string;
  }>;
  notificationsSummary: { unread: number; total: number };
}

export interface CustomerWalletData {
  memberships: CustomerMembership[];
  entitlements: CustomerEntitlement[];
  redemptions: Redemption[];
}

export interface CustomerPunchCardProgress {
  rewardPublicId: string;
  rewardName: string;
  businessName: string;
  punchMode: "VISIT" | "ITEM_QUANTITY";
  qualifyingItemLabel: string | null;
  rewardItemLabel: string;
  requiredPunches: number;
  currentPunches: number;
  completedCards: number;
  punchesRemaining: number;
  lastPunchedAt: string | null;
}

export interface MarketplaceLocation {
  publicId: string;
  name: string;
  addressLine1: string;
  addressLine2: string | null;
  city: string | null;
  district: string | null;
  province: string | null;
  country: string;
  postalCode: string | null;
  landmarkNote: string | null;
  phone: string | null;
  email: string | null;
  publicWebsite: string | null;
  timezone: string;
  latitude: number | null;
  longitude: number | null;
  openingHours: Record<string, string>;
  distanceKm: number | null;
}

export interface MarketplaceBusiness {
  publicId: string;
  slug: string;
  name: string;
  businessType: string;
  logoUrl: string | null;
  primaryColor: string;
  website: string | null;
  rewardCount: number;
  meroCreditsAccepted: boolean;
  nearestDistanceKm: number | null;
  locations: MarketplaceLocation[];
  tierBenefits?: Array<{ code: string; name: string; title: string; description: string }>;
}

export interface DigitalMemberCard {
  publicId: string;
  memberNumber: string;
  qrPayload: string;
  qrExpiresAt: string;
  version: number;
  issuedAt: string;
  reissuedAt: string | null;
  customerName: string;
  tier: {
    code: string;
    name: string;
    rank: number;
    colorHex: string;
    accentHex: string;
    iconKey: string;
    shapeLabel: string;
    foregroundHex: string;
    backgroundHex: string;
    visualAccentHex: string;
    cardPattern: string;
  };
}

export interface BusinessLocationInput {
  name: string;
  code: string;
  addressLine1: string;
  addressLine2?: string | null;
  city: string;
  district?: string | null;
  province?: string | null;
  country: string;
  postalCode?: string | null;
  landmarkNote?: string | null;
  phone?: string | null;
  email?: string | null;
  publicWebsite?: string | null;
  timezone: string;
  latitude: number | null;
  longitude: number | null;
  openingHours: Record<string, string>;
  isPublic: boolean;
  status: "active" | "inactive";
  locationType: string;
}

export interface TenantBusinessLocation extends BusinessLocationInput {
  publicId: string;
  createdAt: string;
  updatedAt: string;
}

export interface GeocodeCandidate {
  id: string;
  label: string;
  latitude: number;
  longitude: number;
  addressLine1: string;
  city: string;
  district: string | null;
  province: string | null;
  country: string;
  postalCode: string | null;
}

export interface CheckoutConfiguration {
  settings: Array<{
    id: string;
    scopeType: string;
    scopeId?: string;
    locationPublicId: string | null;
    currencyCode: string;
    customerConfirmationThresholdMinor: number;
    managerApprovalThresholdMinor: number;
    maximumTransactionMinor: number;
    maximumTransactionsPerCustomerDay: number;
    claimCodeTtlSeconds: number;
    receiptRecoveryWindowDays: number;
    offlineModeEnabled: boolean;
    updatedAt?: string;
  }>;
  locations: CheckoutLocation[];
  rewardRule: {
    publicId: string;
    versionNumber: number;
    spendUnitMinor: number;
    pointsPerUnit: number;
    minimumQualifyingMinor: number;
    maximumPointsPerTransaction: number | null;
    pointExpirationDays: number | null;
  } | null;
}

export interface TenantRewardPointsProgram {
  publicId: string;
  program: { publicId: string; name: string };
  currencyCode: string;
  versionNumber: number;
  spendUnitMinor: number;
  pointsPerUnit: number;
  minimumQualifyingMinor: number;
  maximumPointsPerTransaction: number | null;
  pointExpirationDays: number | null;
  status: "draft" | "active" | "retired";
  effectiveFrom: string;
  effectiveTo: string | null;
  createdAt: string;
}

export interface TenantRewardPointsProgramUpdateInput {
  spendUnitMinor: number;
  pointsPerUnit: number;
  minimumQualifyingMinor: number;
  maximumPointsPerTransaction: number | null;
  pointExpirationDays: number | null;
}

export interface BusinessRedemptionRule {
  publicId: string;
  programPublicId: string;
  programName: string;
  currencyCode: string;
  versionNumber: number;
  pointsPerUnit: number;
  valuePerUnitMinor: number;
  minimumRedemptionPoints: number;
  maximumRedemptionPointsPerTransaction: number | null;
  status: "draft" | "active" | "retired";
  effectiveFrom: string;
  effectiveTo: string | null;
  createdAt: string;
}

export interface BusinessRedemptionRuleInput {
  pointsPerUnit: number;
  valuePerUnitMinor: number;
  minimumRedemptionPoints: number;
  maximumRedemptionPointsPerTransaction: number | null;
}

export interface CheckoutRedemptionQuote {
  tenantPublicId: string;
  tenantSlug: string;
  tenantName: string;
  customerPublicId: string;
  customerName: string;
  pointsBalance: number;
  pointsBalanceValueMinor?: number;
  requestedPoints: number;
  redemptionValueMinor: number;
  netPayableAmountMinor: number;
  maximumRedeemablePoints: number;
  maximumRedemptionValueMinor: number;
  rule: BusinessRedemptionRule;
}

export interface CheckoutAnalytics {
  summary: {
    transactionCount: number;
    confirmedVolumeMinor: number;
    rewardPointsIssued: number;
    tierPointsIssued: number;
    activeCustomers: number;
  } | null;
  byStatus: Array<{ status: string; count: number }>;
  recentRisk: Array<{
    publicId: string;
    eventType: string;
    severity: string;
    status: string;
    score: number;
    createdAt: string;
  }>;
}

export interface TierAdminProgram {
  program: { publicId: string; marketCode: string; currencyCode: string; name: string } | null;
  tiers: Array<{
    publicId: string;
    code: string;
    name: string;
    minimumTierPoints: number;
    colorHex: string;
    accentHex: string;
    description: string;
    benefits: TierBenefit[];
  }>;
  rules: Array<{
    publicId: string;
    versionNumber: number;
    spendUnitMinor: number;
    pointsPerUnit: number;
    minimumQualifyingMinor: number;
    maximumPointsPerTransaction: number | null;
    status: string;
    effectiveFrom: string;
    effectiveTo: string | null;
  }>;
}

export type TierAdminRule = TierAdminProgram["rules"][number];

export interface PlatformSummary {
  tenants: { total: number; active: number; suspended: number };
  users: { total: number; active: number; verified: number };
  customers: { total: number };
  tenantStaff: { total: number };
  rewards: { total: number; active: number };
  redemptions: { total: number };
  tierMembers: { total: number };
  credits: { wallets: number; outstandingMinor: number };
}

export interface PlatformUser {
  publicId: string;
  email: string;
  phone: string | null;
  profileImageUrl: string | null;
  status: "active" | "suspended";
  emailVerifiedAt: string | null;
  emailVerified: boolean;
  firstName: string;
  lastName: string;
  birthday: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  city: string | null;
  district: string | null;
  province: string | null;
  country: string | null;
  postalCode: string | null;
  roles: Array<"customer" | "tenant_staff" | "super_admin">;
  tenantNames: string[];
  createdAt: string;
  updatedAt: string;
}

export interface ArchivedPlatformAccount {
  entityType: "business" | "user";
  publicId: string;
  archivedAt: string;
  releasedIdentifiers: Record<string, unknown>;
  affectedRecords: Record<string, number>;
}

export interface PlatformSettings {
  platformName: string;
  supportEmail: string;
  defaultTimezone: string;
  defaultCountry: string;
  privacyPolicyUrl: string | null;
  termsUrl: string | null;
}

export type PublicTierProgram = Omit<TierAdminProgram, "rules">;

export interface RegisterInput {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  birthday?: string;
  termsAccepted: true;
  marketingEmail: boolean;
}

export interface TenantRegistrationInput {
  slug: string;
  businessName: string;
  legalName?: string;
  businessType: string;
  businessEmail: string;
  businessPhone?: string;
  website?: string;
  defaultCurrency: string;
  defaultTimezone: string;
  country: string;
  address?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  latitude?: number;
  longitude?: number;
  businessPanNumber: string;
  ownerEmail: string;
  ownerFirstName: string;
  ownerLastName: string;
  initialPassword: string;
  subscriptionPlan: "BASIC" | "PRO" | "MASTER";
  billingCycle: "MONTHLY" | "ANNUAL";
  termsAccepted: true;
}

export interface TenantRegistrationResult extends RegistrationResult {
  tenantPublicId: string;
  tenantSlug: string;
  applicationStatus: "PENDING_REVIEW";
  subscriptionPlan: "BASIC" | "PRO" | "MASTER";
  billingCycle: "MONTHLY" | "ANNUAL";
}

export interface BusinessPanAvailability {
  businessPanNumber: string;
  available: boolean;
  message: string;
}

export type VerificationDelivery = "sent" | "already_verified" | "temporarily_unavailable";

export interface RegistrationResult {
  email: string;
  verificationRequired: boolean;
  delivery: VerificationDelivery;
}

export interface LoginInput {
  portal: Portal;
  email: string;
  password: string;
  tenantSlug?: string;
}

export interface CreateTenantInput {
  slug: string;
  businessName: string;
  businessType: string;
  businessEmail: string;
  defaultCurrency: string;
  defaultTimezone: string;
  country: string;
  ownerFirstName: string;
  ownerLastName: string;
  ownerEmail: string;
  initialPassword: string;
}

export interface TenantRewardInput {
  name: string;
  description: string;
  imageUrl?: string | null;
  pointsCost: number;
  inventoryTotal: number;
  status: "draft" | "active" | "inactive";
  featured: boolean;
  startsAt?: string | null;
  endsAt?: string | null;
  internalName?: string;
  rewardType?: string;
  mechanismConfig?: RewardMechanismConfig;
  objective?: string;
  rewardValueMinor?: number | null;
  minimumSpendMinor?: number;
  maximumDiscountMinor?: number | null;
  currencyCode?: string;
  expirationMode?: string;
  expirationDaysAfterIssue?: number | null;
  eligibleTierCodes?: string[];
  eligibleSegmentIds?: string[];
  eligibleLocationPublicIds?: string[];
  channelScope?: string[];
  perMemberLimit?: number;
  globalIssueLimit?: number | null;
  globalRedemptionLimit?: number | null;
  budgetMinor?: number | null;
  stackingMode?: string;
  priority?: number;
  requiresApproval?: boolean;
  termsText?: string;
  eligibilityRules?: TenantRewardEligibilityRule[];
}

export interface TenantRewardTemplate {
  code: string;
  name: string;
  businessType: string;
  description: string;
  reward: Partial<TenantRewardInput>;
}

export interface TenantRewardDashboard {
  summary: {
    totalRewards: number;
    activeRewards: number;
    draftRewards: number;
    scheduledRewards: number;
    pendingApproval: number;
    issuedCount: number;
    redeemedCount: number;
    redemptionRate: number;
    configuredBudgetMinor: number;
    committedMinor: number;
    redeemedValueMinor: number;
    outstandingBusinessPoints: number;
    estimatedLiabilityMinor: number;
    pointLiability: number;
    totalRewardExposureMinor: number;
  };
  byLifecycle: Array<{ status: string; count: number }>;
  byType: Array<{ type: string; count: number }>;
  recentRedemptions: Array<{
    publicId: string;
    rewardTitle: string;
    customerName: string;
    locationName: string | null;
    valueMinor: number;
    currencyCode: string;
    redeemedAt: string;
  }>;
}

export interface TenantRewardAnalyticsMetric {
  opportunityCount: number;
  issuedCount: number;
  claimedCount: number;
  redeemedCount: number;
  expiredCount: number;
  cancelledCount: number;
  reversedCount: number;
  rewardCostMinor: number;
  associatedRevenueMinor: number;
  redemptionRate: number;
}

export interface TenantRewardAnalyticsBreakdown extends TenantRewardAnalyticsMetric {
  publicId: string;
  name: string;
}

export interface TenantRewardTierAnalyticsBreakdown extends TenantRewardAnalyticsMetric {
  code: string;
  name: string;
}

export interface TenantRewardAnalyticsReport {
  window: { from: string; to: string };
  generatedAt: string;
  summary: TenantRewardAnalyticsMetric;
  byReward: TenantRewardAnalyticsBreakdown[];
  byCampaign: TenantRewardAnalyticsBreakdown[];
  byLocation: TenantRewardAnalyticsBreakdown[];
  bySegment: TenantRewardAnalyticsBreakdown[];
  byMeroTier: TenantRewardTierAnalyticsBreakdown[];
  definitions: {
    issued: string;
    claimed: string;
    redeemed: string;
    rewardCost: string;
    associatedRevenue: string;
    tierAttribution: string;
  };
}

export interface TenantRewardCalendarItem {
  publicId: string;
  title: string;
  itemType: string;
  status: string;
  startsAt: string | null;
  endsAt: string | null;
}

export type TenantRewardSegmentMatch = "ALL" | "ANY";
export type TenantRewardSegmentStatus = "draft" | "active" | "archived";

export interface TenantRewardSegmentRule {
  dimension: "CUSTOMER" | "TRANSACTION";
  field: string;
  operator: "EQ" | "NEQ" | "GTE" | "LTE" | "BETWEEN" | "IN" | "NOT_IN";
  value: boolean | number | string[] | [number, number];
}

export interface TenantRewardSegmentConditions {
  match: TenantRewardSegmentMatch;
  rules: TenantRewardSegmentRule[];
}

export interface TenantRewardSegment {
  publicId: string;
  name: string;
  description: string;
  conditions: TenantRewardSegmentConditions;
  estimatedMembers: number;
  status: TenantRewardSegmentStatus;
  estimate: {
    kind: "APPROXIMATE";
    asOf: string | null;
    basis: string;
  };
  createdAt: string | null;
  updatedAt: string | null;
}

export interface TenantRewardSegmentInput {
  name: string;
  description: string;
  conditions: TenantRewardSegmentConditions;
  status: "draft" | "active";
}

export type TenantCampaignChannel = "IN_APP" | "EMAIL" | "PUSH" | "SMS";
export type TenantCampaignAudience = { type: "ALL_ACTIVE_MEMBERS" } | { type: "SEGMENT" };
export type TenantRewardCampaignStatus =
  | "DRAFT"
  | "PENDING_APPROVAL"
  | "APPROVED"
  | "SCHEDULED"
  | "ACTIVE"
  | "PAUSED"
  | "COMPLETED"
  | "REJECTED"
  | "ARCHIVED";
export type TenantCampaignBudgetStatus = "ACTIVE" | "EXHAUSTED" | "CLOSED";

export interface TenantRewardCampaign {
  publicId: string;
  name: string;
  objective: string;
  audience: TenantCampaignAudience;
  channelScope: TenantCampaignChannel[];
  budgetMinor: number | null;
  quantityLimit: number | null;
  issuedCount: number;
  redeemedCount: number;
  status: TenantRewardCampaignStatus;
  lifecycleVersion: number;
  audienceSnapshottedAt: string | null;
  activatedAt: string | null;
  completedAt: string | null;
  budgetCommittedMinor: number;
  budgetRedeemedMinor: number;
  budgetStatus: TenantCampaignBudgetStatus;
  startsAt: string | null;
  endsAt: string | null;
  timezone: string;
  createdAt: string | null;
  updatedAt: string | null;
  reward: { publicId: string; name: string } | null;
  segment: { publicId: string; name: string } | null;
}

export interface TenantRewardCampaignInput {
  name: string;
  objective: string;
  rewardPublicId?: string | null;
  segmentPublicId?: string | null;
  audience: TenantCampaignAudience;
  channelScope: ["IN_APP"];
  budgetMinor?: number | null;
  startsAt?: string | null;
  endsAt?: string | null;
  timezone: string;
}

export interface TenantCampaignExecutionLimitsInput {
  quantityLimit: number | null;
  budgetMinor: number | null;
}

export interface TenantCampaignExecutionLimits extends TenantCampaignExecutionLimitsInput {
  publicId: string;
}

export type TenantRewardReferralStatus =
  "PENDING" | "QUALIFIED" | "REWARDED" | "REJECTED" | "CANCELLED";

export interface TenantRewardReferral {
  publicId: string;
  status: TenantRewardReferralStatus;
  referrerCustomerPublicId: string;
  referrerFirstName: string;
  referrerLastName: string;
  referredCustomerPublicId: string;
  referredFirstName: string;
  referredLastName: string;
  referrerRewardPublicId: string;
  referrerRewardName: string;
  referredRewardPublicId: string | null;
  referredRewardName: string | null;
  campaignPublicId: string | null;
  campaignName: string | null;
  minimumSpendMinor: number;
  currencyCode: string;
  qualifyingTransactionPublicId: string | null;
  referrerIssuancePublicId: string | null;
  referredIssuancePublicId: string | null;
  qualifiedAt: string | null;
  rewardedAt: string | null;
  lastErrorCode: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface TenantRewardReferralInput {
  referrerCustomerPublicId: string;
  referredCustomerPublicId: string;
  referrerRewardPublicId: string;
  referredRewardPublicId?: string | null;
  campaignPublicId?: string | null;
  minimumSpendMinor: number;
  idempotencyKey: string;
}

export interface TenantRewardReferralAttribution {
  publicId: string;
  status: TenantRewardReferralStatus;
  minimumSpendMinor: number;
  currencyCode: string;
  createdAt: string | null;
}

export interface TenantRewardReferralQuery {
  status?: TenantRewardReferralStatus;
  limit?: number;
  offset?: number;
}

export interface TenantCustomerSummary {
  customerPublicId: string;
  membershipPublicId: string;
  firstName: string;
  lastName: string;
  email: string;
  loyaltyNumber: string;
  pointsBalance: number;
  status: string;
}

export interface TenantCustomerSearchQuery {
  q?: string;
  limit?: number;
  offset?: number;
}

export interface TenantRewardIssuance {
  publicId: string;
  reward: { publicId: string; name: string };
  customer: { publicId: string };
  redemptionPublicId: string;
  status: string;
  valueMinor: number;
  issuedAt: string;
  startsAt: string | null;
  expiresAt: string | null;
}

export interface TenantManualRewardIssueInput {
  customerPublicId: string;
  locationPublicId: string;
  reason: string;
  idempotencyKey: string;
}

export interface TenantManualRewardIssueResult {
  issuance: TenantRewardIssuance;
  token: string | null;
  replayed: boolean;
}

export type TenantRewardAutomationTemplate =
  | "WELCOME"
  | "FIRST_PURCHASE"
  | "BIRTHDAY"
  | "MEMBERSHIP_ANNIVERSARY"
  | "MILESTONE"
  | "INACTIVITY"
  | "REWARD_EXPIRING"
  | "POINTS_EXPIRING"
  | "TIER_STATUS"
  | "REFERRAL_QUALIFIED"
  | "SPECIAL_EVENT"
  | "SERVICE_RECOVERY";

export interface TenantRewardAutomation {
  publicId: string;
  name: string;
  templateCode: string;
  trigger: Record<string, unknown>;
  action: Record<string, unknown>;
  timezone: string;
  frequencyCapDays: number | null;
  status: string;
  lastRunAt: string | null;
  nextRunAt: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  reward: { publicId: string; name: string } | null;
}

export interface TenantRewardAutomationInput {
  name: string;
  rewardPublicId?: string | null;
  templateCode: TenantRewardAutomationTemplate;
  trigger: Record<string, unknown>;
  action: Record<string, unknown>;
  timezone: string;
  frequencyCapDays?: number | null;
  status: "DRAFT" | "ACTIVE" | "PAUSED";
  nextRunAt?: string | null;
}

export type TenantRewardAutomationUpdateInput = Partial<
  Omit<TenantRewardAutomationInput, "status">
>;
export type TenantRewardAutomationStatus = "ACTIVE" | "PAUSED" | "ARCHIVED";

export interface TenantRewardRedemption {
  publicId: string;
  redemptionCode: string | null;
  status: string;
  pointsSpent: number;
  valueMinor: number;
  transactionAmountMinor: number | null;
  createdAt: string | null;
  fulfilledAt: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
  rewardPublicId: string;
  rewardName: string;
  rewardType: string | null;
  rewardValueMinor: number | null;
  minimumSpendMinor: number | null;
  maximumDiscountMinor: number | null;
  currencyCode: string;
  termsText: string | null;
  customerPublicId: string;
  customerName: string;
  locationPublicId: string | null;
  locationName: string | null;
  purchaseReference: string | null;
  stackingMode: string | null;
  stackingPriority: number | null;
  stackingSequence: number | null;
  stackingChecked: boolean | null;
  stackingValid: boolean | null;
  stackingCompanionCount: number | null;
  priorityWinnerRewardPublicId: string | null;
  purchaseAmountMinor: number | null;
  appliedValueMinor: number | null;
  valid: boolean | null;
  validationCode: string | null;
  validationMessage: string | null;
  locationRequired: boolean | null;
  locationValid: boolean | null;
  mechanismConfig?: RewardMechanismConfig | Record<string, never>;
  mechanismTerms?: string[];
  mechanismSelection?: { id: string; label: string; liabilityMinor: number } | null;
}

export interface TenantRewardLineItemFact {
  productCode?: string;
  categoryCode?: string;
  quantity: number;
}

export interface TenantRewardRedemptionCheckInput {
  redemptionCode: string;
  locationPublicId?: string;
  purchaseAmountMinor?: number;
  purchaseReference?: string;
  channel?: string;
  lineItems?: TenantRewardLineItemFact[];
  mechanismSelectionId?: string;
}

export interface TenantRewardFulfillmentInput {
  locationPublicId?: string;
  purchaseAmountMinor?: number;
  purchaseReference?: string;
  channel?: string;
  lineItems?: TenantRewardLineItemFact[];
  mechanismSelectionId?: string;
}

export interface TenantRewardAuditEvent {
  publicId: string;
  action: string;
  entityType: string;
  entityId: string;
  before: unknown;
  after: unknown;
  createdAt: string | null;
  actorName: string | null;
}

export interface BootstrapSuperAdminInput {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  bootstrapSecret: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;

  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const asString = (value: unknown, fallback = "") => (typeof value === "string" ? value : fallback);

const asNumber = (value: unknown, fallback = 0) => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const asBoolean = (value: unknown, fallback = false) =>
  typeof value === "boolean" ? value : fallback;

const nullableString = (value: unknown) =>
  typeof value === "string" && value.length > 0 ? value : null;

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

const normalizeRewardStatus = (value: unknown): TenantRewardLifecycleStatus => {
  const normalized = asString(value, "draft")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (normalized === "pendingapproval") return "pending_approval";
  if (
    normalized === "draft" ||
    normalized === "pending_approval" ||
    normalized === "approved" ||
    normalized === "scheduled" ||
    normalized === "active" ||
    normalized === "paused" ||
    normalized === "expired" ||
    normalized === "archived" ||
    normalized === "rejected" ||
    normalized === "inactive"
  )
    return normalized;
  return "draft";
};

const normalizeEligibilityRules = (value: unknown): TenantRewardEligibilityRule[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!isRecord(item)) return [];
    const dimension = asString(pick(item, "dimension"));
    const field = asString(pick(item, "field"));
    const operator = asString(pick(item, "operator"));
    const rawValue = pick(item, "value");
    if (!dimension || !field || !operator || rawValue === undefined) return [];
    return [{ dimension, field, operator, value: rawValue }];
  });
};

const pick = (record: Record<string, unknown>, ...keys: string[]) => {
  for (const key of keys) {
    if (record[key] !== undefined && record[key] !== null) return record[key];
  }
  return undefined;
};

const unwrap = (payload: unknown): unknown => {
  if (!isRecord(payload)) return payload;
  return payload.data ?? payload.result ?? payload;
};

const getItems = (payload: unknown, key: string): unknown[] => {
  const unwrapped = unwrap(payload);
  if (Array.isArray(unwrapped)) return unwrapped;
  if (!isRecord(unwrapped)) return [];
  const collection = unwrapped.items ?? unwrapped[key];
  return Array.isArray(collection) ? collection : [];
};

const parseMessage = (payload: unknown, fallback: string) => {
  if (!isRecord(payload)) return fallback;
  const direct = pick(payload, "message", "error", "detail");
  if (typeof direct === "string") return direct;
  if (isRecord(direct)) {
    return asString(pick(direct, "message", "detail"), fallback);
  }
  return fallback;
};

const parseErrorCode = (payload: unknown) => {
  if (!isRecord(payload)) return undefined;
  const error = payload.error;
  if (!isRecord(error)) return undefined;
  const code = error.code;
  return typeof code === "string" ? code : undefined;
};

async function request(path: string, init?: RequestInit): Promise<unknown> {
  const headers = new Headers(init?.headers);
  headers.set("Accept", "application/json");
  if (init?.body && !(init.body instanceof FormData))
    headers.set("Content-Type", "application/json");
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers,
  });

  const contentType = response.headers.get("content-type") ?? "";
  const payload: unknown = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  if (!response.ok) {
    throw new ApiError(
      parseMessage(payload, `Request failed (${String(response.status)}).`),
      response.status,
      parseErrorCode(payload),
    );
  }

  return payload;
}

async function uploadMediaFile(path: string, file: File): Promise<string> {
  const response = await fetch(path, {
    method: "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      "Content-Type": file.type,
    },
    body: file,
  });
  const payload: unknown = await response.json();
  if (!response.ok) {
    throw new ApiError(
      parseMessage(payload, `Upload failed (${String(response.status)}).`),
      response.status,
      parseErrorCode(payload),
    );
  }
  const result = dataRecord(payload);
  if (typeof result.url !== "string") throw new ApiError("The uploaded image URL is missing.", 500);
  return result.url;
}

export async function uploadCustomerProfileImage(file: File): Promise<string> {
  return uploadMediaFile("/api/v1/customer/media/profile", file);
}

export async function uploadBusinessOwnerProfileImage(file: File): Promise<string> {
  return uploadMediaFile("/api/v1/tenant/media/profile", file);
}

export async function uploadTenantOfferImage(file: File): Promise<string> {
  return uploadMediaFile("/api/v1/tenant/media/offer", file);
}

function normalizeReward(value: unknown, index: number): Reward | null {
  if (!isRecord(value)) return null;
  const tenant = isRecord(value.tenant) ? value.tenant : {};
  const publicId = asString(pick(value, "publicId", "public_id", "id"), `reward-${String(index)}`);
  const title = asString(pick(value, "title", "name"));
  if (!title) return null;
  const lifecycleStatus = normalizeRewardStatus(
    pick(value, "lifecycleStatus", "lifecycle_status", "status"),
  );
  const inventoryTotal = asNumber(
    pick(
      value,
      "inventoryTotal",
      "inventory_total",
      "globalIssueLimit",
      "global_issue_limit",
      "inventoryRemaining",
    ),
    0,
  );
  return {
    publicId,
    tenantPublicId: asString(
      pick(value, "tenantPublicId", "tenant_public_id", "tenantId") ??
        pick(tenant, "publicId", "public_id", "id"),
    ),
    tenantName: asString(
      pick(value, "tenantName", "tenant_name", "merchantName", "businessName", "business_name") ??
        pick(tenant, "businessName", "business_name", "name"),
      "Mero Perks partner",
    ),
    tenantSlug: asString(
      pick(value, "tenantSlug", "tenant_slug") ?? pick(tenant, "slug"),
      "partner",
    ),
    title,
    description: asString(
      pick(value, "description", "details"),
      "A special reward from this partner.",
    ),
    category: asString(pick(value, "category", "rewardType", "reward_type"), "Everyday"),
    pointsCost: asNumber(
      pick(value, "pointsCost", "points_cost", "costPoints", "cost_points", "points"),
    ),
    monetaryValue: nullableString(pick(value, "monetaryValue", "monetary_value", "value")),
    expiresAt: nullableString(
      pick(value, "expiresAt", "expires_at", "expiryDate", "availableUntil", "endsAt"),
    ),
    imageUrl: nullableString(pick(value, "imageUrl", "image_url", "image")),
    available:
      lifecycleStatus === "active" &&
      asNumber(pick(value, "inventoryRemaining", "inventory_remaining"), 1) !== 0 &&
      asBoolean(pick(value, "available", "isAvailable", "is_active"), true),
    featured: asBoolean(pick(value, "featured", "isFeatured", "is_featured")),
    status: lifecycleStatus,
    inventoryTotal,
    inventoryRemaining: asNumber(
      pick(value, "inventoryRemaining", "inventory_remaining"),
      inventoryTotal,
    ),
    redeemedCount: asNumber(
      pick(value, "redeemedCount", "redeemed_count", "redemptionCount", "redemption_count"),
    ),
    internalName: asString(pick(value, "internalName", "internal_name"), title),
    rewardType: asString(pick(value, "rewardType", "reward_type", "category"), "points_redemption"),
    mechanismConfig: isRecord(pick(value, "mechanismConfig", "mechanism_config"))
      ? (pick(value, "mechanismConfig", "mechanism_config") as RewardMechanismConfig)
      : {},
    mechanismLiabilityMinor: asNumber(
      pick(value, "mechanismLiabilityMinor", "mechanism_liability_minor"),
    ),
    mechanismTerms: asStringArray(pick(value, "mechanismTerms", "mechanism_terms")),
    objective: asString(pick(value, "objective"), "retention"),
    rewardValueMinor:
      pick(value, "rewardValueMinor", "reward_value_minor") === undefined
        ? null
        : asNumber(pick(value, "rewardValueMinor", "reward_value_minor")),
    minimumSpendMinor: asNumber(pick(value, "minimumSpendMinor", "minimum_spend_minor")),
    maximumDiscountMinor:
      pick(value, "maximumDiscountMinor", "maximum_discount_minor") === undefined
        ? null
        : asNumber(pick(value, "maximumDiscountMinor", "maximum_discount_minor")),
    currencyCode: asString(pick(value, "currencyCode", "currency_code"), "NPR"),
    expirationMode: asString(pick(value, "expirationMode", "expiration_mode"), "fixed_date"),
    expirationDaysAfterIssue:
      pick(value, "expirationDaysAfterIssue", "expiration_days_after_issue") === undefined
        ? null
        : asNumber(pick(value, "expirationDaysAfterIssue", "expiration_days_after_issue")),
    eligibleTierCodes: asStringArray(pick(value, "eligibleTierCodes", "eligible_tier_codes")),
    eligibleSegmentIds: asStringArray(pick(value, "eligibleSegmentIds", "eligible_segment_ids")),
    eligibleLocationPublicIds: asStringArray(
      pick(value, "eligibleLocationPublicIds", "eligible_location_public_ids"),
    ),
    eligibleLocationNames: asStringArray(
      pick(value, "eligibleLocationNames", "eligible_location_names", "eligibleLocations"),
    ),
    channelScope: asStringArray(pick(value, "channelScope", "channel_scope")),
    perMemberLimit: asNumber(pick(value, "perMemberLimit", "per_member_limit"), 1),
    globalIssueLimit:
      pick(value, "globalIssueLimit", "global_issue_limit") === undefined
        ? null
        : asNumber(pick(value, "globalIssueLimit", "global_issue_limit")),
    globalRedemptionLimit:
      pick(value, "globalRedemptionLimit", "global_redemption_limit") === undefined
        ? null
        : asNumber(pick(value, "globalRedemptionLimit", "global_redemption_limit")),
    budgetMinor:
      pick(value, "budgetMinor", "budget_minor") === undefined
        ? null
        : asNumber(pick(value, "budgetMinor", "budget_minor")),
    stackingMode: asString(pick(value, "stackingMode", "stacking_mode"), "not_stackable"),
    priority: asNumber(pick(value, "priority"), 100),
    requiresApproval: asBoolean(pick(value, "requiresApproval", "requires_approval")),
    termsText: asString(pick(value, "termsText", "terms_text")),
    eligibilityRules: normalizeEligibilityRules(
      pick(value, "eligibilityRules", "eligibility_rules"),
    ),
    lifecycleStatus,
    lifecycleVersion: asNumber(pick(value, "lifecycleVersion", "lifecycle_version"), 1),
    startsAt: nullableString(pick(value, "startsAt", "starts_at")),
    endsAt: nullableString(pick(value, "endsAt", "ends_at", "expiresAt", "expires_at")),
    createdAt: nullableString(pick(value, "createdAt", "created_at")),
    updatedAt: nullableString(pick(value, "updatedAt", "updated_at")),
  };
}

const nullableNumber = (value: unknown) =>
  value === undefined || value === null ? null : asNumber(value);
const nullableBoolean = (value: unknown) => (typeof value === "boolean" ? value : null);

function normalizeNamedReference(value: unknown): { publicId: string; name: string } | null {
  if (!isRecord(value)) return null;
  const publicId = asString(pick(value, "publicId", "public_id", "id"));
  if (!publicId) return null;
  return { publicId, name: asString(pick(value, "name", "title"), "Untitled") };
}

function normalizeTenantRewardCalendarItem(
  value: unknown,
  index: number,
): TenantRewardCalendarItem | null {
  if (!isRecord(value)) return null;
  const title = asString(pick(value, "title", "name"));
  if (!title) return null;
  return {
    publicId: asString(pick(value, "publicId", "public_id", "id"), `calendar-${String(index)}`),
    title,
    itemType: asString(pick(value, "itemType", "item_type", "type"), "EVENT"),
    status: asString(pick(value, "status"), "DRAFT"),
    startsAt: nullableString(pick(value, "startsAt", "starts_at")),
    endsAt: nullableString(pick(value, "endsAt", "ends_at")),
  };
}

function normalizeTenantRewardSegmentRule(value: unknown): TenantRewardSegmentRule | null {
  if (!isRecord(value)) return null;
  const dimension = asString(pick(value, "dimension"));
  const field = asString(pick(value, "field"));
  const operator = asString(pick(value, "operator"));
  if (
    !(dimension === "CUSTOMER" || dimension === "TRANSACTION") ||
    !field ||
    !["EQ", "NEQ", "GTE", "LTE", "BETWEEN", "IN", "NOT_IN"].includes(operator)
  )
    return null;
  const rawValue = pick(value, "value");
  let normalizedValue: TenantRewardSegmentRule["value"] | null = null;
  if (
    typeof rawValue === "boolean" ||
    (typeof rawValue === "number" && Number.isFinite(rawValue))
  ) {
    normalizedValue = rawValue;
  } else if (
    Array.isArray(rawValue) &&
    rawValue.every((item): item is string => typeof item === "string")
  ) {
    normalizedValue = rawValue;
  } else if (
    Array.isArray(rawValue) &&
    rawValue.length === 2 &&
    typeof rawValue[0] === "number" &&
    Number.isFinite(rawValue[0]) &&
    typeof rawValue[1] === "number" &&
    Number.isFinite(rawValue[1])
  ) {
    normalizedValue = [rawValue[0], rawValue[1]];
  }
  if (normalizedValue === null) return null;
  return {
    dimension,
    field,
    operator: operator as TenantRewardSegmentRule["operator"],
    value: normalizedValue,
  };
}

function normalizeTenantRewardSegment(value: unknown): TenantRewardSegment | null {
  if (!isRecord(value)) return null;
  const publicId = asString(pick(value, "publicId", "public_id", "id"));
  const name = asString(pick(value, "name", "title"));
  if (!publicId || !name) return null;
  let rawConditions: unknown = pick(value, "conditions", "conditionsJson", "conditions_json");
  if (typeof rawConditions === "string") {
    try {
      rawConditions = JSON.parse(rawConditions) as unknown;
    } catch {
      return null;
    }
  }
  if (!isRecord(rawConditions)) return null;
  const match = asString(pick(rawConditions, "match"));
  const rawRules = pick(rawConditions, "rules");
  if (!(match === "ALL" || match === "ANY") || !Array.isArray(rawRules)) return null;
  const rules = rawRules
    .map(normalizeTenantRewardSegmentRule)
    .filter((rule): rule is TenantRewardSegmentRule => rule !== null);
  if (rules.length !== rawRules.length || rules.length === 0) return null;
  const rawStatus = asString(pick(value, "status"), "draft").toLowerCase();
  const status: TenantRewardSegmentStatus = ["draft", "active", "archived"].includes(rawStatus)
    ? (rawStatus as TenantRewardSegmentStatus)
    : "draft";
  const estimate = isRecord(value.estimate) ? value.estimate : {};
  return {
    publicId,
    name,
    description: asString(pick(value, "description")),
    conditions: { match, rules },
    estimatedMembers: asNumber(pick(value, "estimatedMembers", "estimated_members")),
    status,
    estimate: {
      kind: "APPROXIMATE",
      asOf: nullableString(
        pick(estimate, "asOf", "as_of") ?? pick(value, "updatedAt", "updated_at"),
      ),
      basis: asString(
        pick(estimate, "basis"),
        "Approximate audience calculated from current tenant membership data.",
      ),
    },
    createdAt: nullableString(pick(value, "createdAt", "created_at")),
    updatedAt: nullableString(pick(value, "updatedAt", "updated_at")),
  };
}

const normalizeTenantRewardCampaignStatus = (value: unknown): TenantRewardCampaignStatus => {
  const status = asString(value, "DRAFT").toUpperCase();
  return [
    "DRAFT",
    "PENDING_APPROVAL",
    "APPROVED",
    "SCHEDULED",
    "ACTIVE",
    "PAUSED",
    "COMPLETED",
    "REJECTED",
    "ARCHIVED",
  ].includes(status)
    ? (status as TenantRewardCampaignStatus)
    : "DRAFT";
};

function normalizeTenantRewardCampaign(value: unknown, index: number): TenantRewardCampaign | null {
  if (!isRecord(value)) return null;
  const name = asString(pick(value, "name", "title"));
  if (!name) return null;
  const channels = asStringArray(pick(value, "channelScope", "channel_scope")).filter(
    (channel): channel is TenantCampaignChannel =>
      ["IN_APP", "EMAIL", "PUSH", "SMS"].includes(channel),
  );
  const rawBudgetStatus = asString(
    pick(value, "budgetStatus", "budget_status"),
    "ACTIVE",
  ).toUpperCase();
  const budgetStatus: TenantCampaignBudgetStatus = ["ACTIVE", "EXHAUSTED", "CLOSED"].includes(
    rawBudgetStatus,
  )
    ? (rawBudgetStatus as TenantCampaignBudgetStatus)
    : "ACTIVE";
  const segment = normalizeNamedReference(value.segment);
  const rawAudience = isRecord(value.audience) ? value.audience : null;
  const audience: TenantCampaignAudience =
    rawAudience?.type === "SEGMENT" && segment !== null
      ? { type: "SEGMENT" }
      : { type: "ALL_ACTIVE_MEMBERS" };
  return {
    publicId: asString(pick(value, "publicId", "public_id", "id"), `campaign-${String(index)}`),
    name,
    objective: asString(pick(value, "objective"), "CUSTOM"),
    audience,
    channelScope: channels,
    budgetMinor: nullableNumber(pick(value, "budgetMinor", "budget_minor")),
    quantityLimit: nullableNumber(pick(value, "quantityLimit", "quantity_limit")),
    issuedCount: asNumber(pick(value, "issuedCount", "issued_count")),
    redeemedCount: asNumber(pick(value, "redeemedCount", "redeemed_count")),
    status: normalizeTenantRewardCampaignStatus(pick(value, "status")),
    lifecycleVersion: asNumber(pick(value, "lifecycleVersion", "lifecycle_version"), 1),
    audienceSnapshottedAt: nullableString(
      pick(value, "audienceSnapshottedAt", "audience_snapshotted_at"),
    ),
    activatedAt: nullableString(pick(value, "activatedAt", "activated_at")),
    completedAt: nullableString(pick(value, "completedAt", "completed_at")),
    budgetCommittedMinor: asNumber(pick(value, "budgetCommittedMinor", "budget_committed_minor")),
    budgetRedeemedMinor: asNumber(pick(value, "budgetRedeemedMinor", "budget_redeemed_minor")),
    budgetStatus,
    startsAt: nullableString(pick(value, "startsAt", "starts_at")),
    endsAt: nullableString(pick(value, "endsAt", "ends_at")),
    timezone: asString(pick(value, "timezone"), "Asia/Kathmandu"),
    createdAt: nullableString(pick(value, "createdAt", "created_at")),
    updatedAt: nullableString(pick(value, "updatedAt", "updated_at")),
    reward: normalizeNamedReference(value.reward),
    segment,
  };
}

const normalizeTenantRewardReferralStatus = (value: unknown): TenantRewardReferralStatus => {
  const status = asString(value, "PENDING").toUpperCase();
  return ["PENDING", "QUALIFIED", "REWARDED", "REJECTED", "CANCELLED"].includes(status)
    ? (status as TenantRewardReferralStatus)
    : "PENDING";
};

function normalizeTenantRewardReferral(value: unknown): TenantRewardReferral | null {
  if (!isRecord(value)) return null;
  const publicId = asString(pick(value, "publicId", "public_id"));
  const referrerCustomerPublicId = asString(
    pick(value, "referrerCustomerPublicId", "referrer_customer_public_id"),
  );
  const referredCustomerPublicId = asString(
    pick(value, "referredCustomerPublicId", "referred_customer_public_id"),
  );
  const referrerRewardPublicId = asString(
    pick(value, "referrerRewardPublicId", "referrer_reward_public_id"),
  );
  if (
    !publicId ||
    !referrerCustomerPublicId ||
    !referredCustomerPublicId ||
    !referrerRewardPublicId
  )
    return null;
  return {
    publicId,
    status: normalizeTenantRewardReferralStatus(pick(value, "status")),
    referrerCustomerPublicId,
    referrerFirstName: asString(pick(value, "referrerFirstName", "referrer_first_name")),
    referrerLastName: asString(pick(value, "referrerLastName", "referrer_last_name")),
    referredCustomerPublicId,
    referredFirstName: asString(pick(value, "referredFirstName", "referred_first_name")),
    referredLastName: asString(pick(value, "referredLastName", "referred_last_name")),
    referrerRewardPublicId,
    referrerRewardName: asString(
      pick(value, "referrerRewardName", "referrer_reward_name"),
      "Referral reward",
    ),
    referredRewardPublicId: nullableString(
      pick(value, "referredRewardPublicId", "referred_reward_public_id"),
    ),
    referredRewardName: nullableString(pick(value, "referredRewardName", "referred_reward_name")),
    campaignPublicId: nullableString(pick(value, "campaignPublicId", "campaign_public_id")),
    campaignName: nullableString(pick(value, "campaignName", "campaign_name")),
    minimumSpendMinor: asNumber(pick(value, "minimumSpendMinor", "minimum_spend_minor")),
    currencyCode: asString(pick(value, "currencyCode", "currency_code"), "NPR"),
    qualifyingTransactionPublicId: nullableString(
      pick(value, "qualifyingTransactionPublicId", "qualifying_transaction_public_id"),
    ),
    referrerIssuancePublicId: nullableString(
      pick(value, "referrerIssuancePublicId", "referrer_issuance_public_id"),
    ),
    referredIssuancePublicId: nullableString(
      pick(value, "referredIssuancePublicId", "referred_issuance_public_id"),
    ),
    qualifiedAt: nullableString(pick(value, "qualifiedAt", "qualified_at")),
    rewardedAt: nullableString(pick(value, "rewardedAt", "rewarded_at")),
    lastErrorCode: nullableString(pick(value, "lastErrorCode", "last_error_code")),
    createdAt: nullableString(pick(value, "createdAt", "created_at")),
    updatedAt: nullableString(pick(value, "updatedAt", "updated_at")),
  };
}

function normalizeTenantCustomerSummary(value: unknown): TenantCustomerSummary | null {
  if (!isRecord(value)) return null;
  const customerPublicId = asString(pick(value, "customerPublicId", "customer_public_id"));
  const membershipPublicId = asString(pick(value, "membershipPublicId", "membership_public_id"));
  if (!customerPublicId || !membershipPublicId) return null;
  return {
    customerPublicId,
    membershipPublicId,
    firstName: asString(pick(value, "firstName", "first_name")),
    lastName: asString(pick(value, "lastName", "last_name")),
    email: asString(pick(value, "email")),
    loyaltyNumber: asString(pick(value, "loyaltyNumber", "loyalty_number")),
    pointsBalance: asNumber(pick(value, "pointsBalance", "points_balance")),
    status: asString(pick(value, "status"), "active"),
  };
}

function normalizeTenantRewardIssuance(value: unknown): TenantRewardIssuance | null {
  if (!isRecord(value)) return null;
  const publicId = asString(pick(value, "publicId", "public_id"));
  const redemptionPublicId = asString(pick(value, "redemptionPublicId", "redemption_public_id"));
  const reward = isRecord(value.reward) ? value.reward : {};
  const customer = isRecord(value.customer) ? value.customer : {};
  const rewardPublicId = asString(pick(reward, "publicId", "public_id"));
  const rewardName = asString(pick(reward, "name", "title"));
  const customerPublicId = asString(pick(customer, "publicId", "public_id"));
  const issuedAt = asString(pick(value, "issuedAt", "issued_at"));
  if (
    !publicId ||
    !redemptionPublicId ||
    !rewardPublicId ||
    !rewardName ||
    !customerPublicId ||
    !issuedAt
  ) {
    return null;
  }
  return {
    publicId,
    reward: { publicId: rewardPublicId, name: rewardName },
    customer: { publicId: customerPublicId },
    redemptionPublicId,
    status: asString(pick(value, "status"), "ISSUED"),
    valueMinor: asNumber(pick(value, "valueMinor", "value_minor")),
    issuedAt,
    startsAt: nullableString(pick(value, "startsAt", "starts_at")),
    expiresAt: nullableString(pick(value, "expiresAt", "expires_at")),
  };
}

function normalizeTenantRewardAutomation(
  value: unknown,
  index: number,
): TenantRewardAutomation | null {
  if (!isRecord(value)) return null;
  const name = asString(pick(value, "name", "title"));
  if (!name) return null;
  return {
    publicId: asString(pick(value, "publicId", "public_id", "id"), `automation-${String(index)}`),
    name,
    templateCode: asString(pick(value, "templateCode", "template_code"), "SPECIAL_EVENT"),
    trigger: isRecord(value.trigger) ? value.trigger : {},
    action: isRecord(value.action) ? value.action : {},
    timezone: asString(pick(value, "timezone"), "Asia/Kathmandu"),
    frequencyCapDays: nullableNumber(pick(value, "frequencyCapDays", "frequency_cap_days")),
    status: asString(pick(value, "status"), "DRAFT"),
    lastRunAt: nullableString(pick(value, "lastRunAt", "last_run_at")),
    nextRunAt: nullableString(pick(value, "nextRunAt", "next_run_at")),
    createdAt: nullableString(pick(value, "createdAt", "created_at")),
    updatedAt: nullableString(pick(value, "updatedAt", "updated_at")),
    reward: normalizeNamedReference(value.reward),
  };
}

function normalizeTenantRewardRedemption(value: unknown): TenantRewardRedemption | null {
  if (!isRecord(value)) return null;
  const publicId = asString(pick(value, "publicId", "public_id", "id"));
  if (!publicId) return null;
  const mechanismSelection = pick(value, "mechanismSelection", "mechanism_selection");
  return {
    publicId,
    redemptionCode: nullableString(pick(value, "redemptionCode", "redemption_code", "code")),
    status: asString(pick(value, "status"), "issued"),
    pointsSpent: asNumber(pick(value, "pointsSpent", "points_spent")),
    valueMinor: asNumber(pick(value, "valueMinor", "value_minor")),
    transactionAmountMinor: nullableNumber(
      pick(value, "transactionAmountMinor", "transaction_amount_minor"),
    ),
    createdAt: nullableString(pick(value, "createdAt", "created_at")),
    fulfilledAt: nullableString(pick(value, "fulfilledAt", "fulfilled_at")),
    reversedAt: nullableString(pick(value, "reversedAt", "reversed_at")),
    reversalReason: nullableString(pick(value, "reversalReason", "reversal_reason")),
    rewardPublicId: asString(pick(value, "rewardPublicId", "reward_public_id")),
    rewardName: asString(
      pick(value, "rewardName", "reward_name", "rewardTitle", "reward_title"),
      "Reward",
    ),
    rewardType: nullableString(pick(value, "rewardType", "reward_type")),
    rewardValueMinor: nullableNumber(pick(value, "rewardValueMinor", "reward_value_minor")),
    minimumSpendMinor: nullableNumber(pick(value, "minimumSpendMinor", "minimum_spend_minor")),
    maximumDiscountMinor: nullableNumber(
      pick(value, "maximumDiscountMinor", "maximum_discount_minor"),
    ),
    currencyCode: asString(pick(value, "currencyCode", "currency_code"), "NPR"),
    termsText: nullableString(pick(value, "termsText", "terms_text")),
    customerPublicId: asString(pick(value, "customerPublicId", "customer_public_id")),
    customerName: asString(pick(value, "customerName", "customer_name"), "Member"),
    locationPublicId: nullableString(pick(value, "locationPublicId", "location_public_id")),
    locationName: nullableString(pick(value, "locationName", "location_name")),
    purchaseReference: nullableString(
      pick(value, "purchaseReference", "purchase_transaction_reference"),
    ),
    stackingMode: nullableString(pick(value, "stackingMode", "applied_stacking_mode")),
    stackingPriority: nullableNumber(
      pick(value, "stackingPriority", "applied_stacking_priority", "priority"),
    ),
    stackingSequence: nullableNumber(pick(value, "stackingSequence", "stacking_sequence")),
    stackingChecked: nullableBoolean(pick(value, "stackingChecked", "stacking_checked")),
    stackingValid: nullableBoolean(pick(value, "stackingValid", "stacking_valid")),
    stackingCompanionCount: nullableNumber(
      pick(value, "stackingCompanionCount", "stacking_companion_count"),
    ),
    priorityWinnerRewardPublicId: nullableString(
      pick(value, "priorityWinnerRewardPublicId", "priority_winner_reward_public_id"),
    ),
    purchaseAmountMinor: nullableNumber(
      pick(value, "purchaseAmountMinor", "purchase_amount_minor"),
    ),
    appliedValueMinor: nullableNumber(pick(value, "appliedValueMinor", "applied_value_minor")),
    valid: nullableBoolean(pick(value, "valid", "isValid", "is_valid")),
    validationCode: nullableString(pick(value, "validationCode", "validation_code")),
    validationMessage: nullableString(pick(value, "validationMessage", "validation_message")),
    locationRequired: nullableBoolean(pick(value, "locationRequired", "location_required")),
    locationValid: nullableBoolean(pick(value, "locationValid", "location_valid")),
    mechanismConfig: isRecord(pick(value, "mechanismConfig", "mechanism_config"))
      ? (pick(value, "mechanismConfig", "mechanism_config") as RewardMechanismConfig)
      : {},
    mechanismTerms: asStringArray(pick(value, "mechanismTerms", "mechanism_terms")),
    mechanismSelection: isRecord(mechanismSelection)
      ? {
          id: asString(pick(mechanismSelection, "id")),
          label: asString(pick(mechanismSelection, "label")),
          liabilityMinor: asNumber(pick(mechanismSelection, "liabilityMinor", "liability_minor")),
        }
      : null,
  };
}

function normalizeTenantRewardAuditEvent(
  value: unknown,
  index: number,
): TenantRewardAuditEvent | null {
  if (!isRecord(value)) return null;
  const action = asString(pick(value, "action"));
  if (!action) return null;
  return {
    publicId: asString(pick(value, "publicId", "public_id", "id"), `audit-${String(index)}`),
    action,
    entityType: asString(pick(value, "entityType", "entity_type"), "reward"),
    entityId: asString(pick(value, "entityId", "entity_id")),
    before: value.before ?? null,
    after: value.after ?? null,
    createdAt: nullableString(pick(value, "createdAt", "created_at")),
    actorName: nullableString(pick(value, "actorName", "actor_name")),
  };
}

function normalizeSession(payload: unknown, fallbackType?: SessionType): SessionUser | null {
  const root = unwrap(payload);
  if (!isRecord(root)) return null;
  const data = isRecord(root.context) ? root.context : root;
  const user = isRecord(data.user) ? data.user : isRecord(root.user) ? root.user : data;
  const profile = isRecord(data.profile)
    ? data.profile
    : isRecord(root.profile)
      ? root.profile
      : isRecord(user.profile)
        ? user.profile
        : {};
  const tenant = isRecord(data.tenant)
    ? data.tenant
    : isRecord(root.tenant)
      ? root.tenant
      : isRecord(user.tenant)
        ? user.tenant
        : {};
  const rawType = asString(
    pick(data, "sessionType", "session_type", "role") ??
      pick(user, "sessionType", "session_type", "role"),
    fallbackType ?? "customer",
  );
  const sessionType: SessionType =
    rawType === "super_admin"
      ? "super_admin"
      : rawType === "tenant_staff" || rawType === "tenant"
        ? "tenant_staff"
        : "customer";
  const firstName = asString(
    pick(user, "firstName", "first_name") ?? pick(profile, "firstName", "first_name"),
  );
  const lastName = asString(
    pick(user, "lastName", "last_name") ?? pick(profile, "lastName", "last_name"),
  );
  const explicitName = asString(pick(user, "name", "displayName", "display_name"));
  const email = asString(pick(user, "email"));
  if (!email && !explicitName && !firstName) return null;
  const roles = asStringArray(pick(data, "roles") ?? pick(user, "roles")) as TenantRole[];
  const permissions = asStringArray(
    pick(data, "permissions", "effectivePermissions", "effective_permissions") ??
      pick(user, "permissions", "effectivePermissions", "effective_permissions"),
  ) as Permission[];
  const allowedLocationIds = asStringArray(
    pick(data, "allowedLocationIds", "allowed_location_ids") ??
      pick(user, "allowedLocationIds", "allowed_location_ids"),
  );
  return {
    publicId: asString(pick(user, "publicId", "public_id", "id", "userId", "user_id"), email),
    name: explicitName || `${firstName} ${lastName}`.trim() || email,
    email,
    sessionType,
    tenantName: nullableString(
      pick(data, "tenantName", "tenant_name", "businessName", "business_name") ??
        pick(tenant, "businessName", "business_name", "name"),
    ),
    tenantSlug: nullableString(pick(data, "tenantSlug", "tenant_slug") ?? pick(tenant, "slug")),
    pointsBalance: asNumber(
      pick(data, "pointsBalance", "points_balance") ??
        pick(user, "pointsBalance", "points_balance"),
    ),
    roles: sessionType === "tenant_staff" ? roles : [],
    permissions: sessionType === "tenant_staff" ? permissions : [],
    allowedLocationIds: sessionType === "tenant_staff" ? allowedLocationIds : [],
  };
}

function normalizeTenant(value: unknown, index: number): Tenant | null {
  if (!isRecord(value)) return null;
  const name = asString(
    pick(value, "businessName", "business_name", "name", "displayName", "display_name"),
  );
  if (!name) return null;
  const rawStatus = asString(pick(value, "status"), "trial");
  const status: Tenant["status"] =
    rawStatus === "active" ||
    rawStatus === "suspended" ||
    rawStatus === "cancelled" ||
    rawStatus === "archived"
      ? rawStatus
      : "trial";
  const counts = isRecord(value.counts) ? value.counts : {};
  return {
    publicId: asString(pick(value, "publicId", "public_id", "id"), `tenant-${String(index)}`),
    name,
    slug: asString(pick(value, "slug")),
    status,
    contactEmail: asString(
      pick(value, "businessEmail", "business_email", "contactEmail", "contact_email", "email"),
      "—",
    ),
    legalName: nullableString(pick(value, "legalName", "legal_name")),
    businessType: asString(pick(value, "businessType", "business_type"), "retail"),
    businessPhone: nullableString(pick(value, "businessPhone", "business_phone")),
    website: nullableString(pick(value, "website")),
    defaultCurrency: asString(pick(value, "defaultCurrency", "default_currency"), "NPR"),
    defaultTimezone: asString(pick(value, "defaultTimezone", "default_timezone"), "UTC"),
    country: asString(pick(value, "country"), "Nepal"),
    address: nullableString(pick(value, "address")),
    city: nullableString(pick(value, "city")),
    stateProvince: nullableString(pick(value, "stateProvince", "state_province")),
    postalCode: nullableString(pick(value, "postalCode", "postal_code")),
    logoUrl: nullableString(pick(value, "logoUrl", "logo_url")),
    primaryColor: asString(pick(value, "primaryColor", "primary_color"), "#C8102E"),
    secondaryColor: asString(pick(value, "secondaryColor", "secondary_color"), "#F7F5F0"),
    accentColor: asString(pick(value, "accentColor", "accent_color"), "#F5A623"),
    plan: isRecord(value.plan)
      ? { id: asString(value.plan.id), name: asString(value.plan.name) }
      : null,
    createdAt: nullableString(pick(value, "createdAt", "created_at")),
    locationCount: asNumber(
      pick(value, "locationCount", "location_count") ?? pick(counts, "locations"),
    ),
    staffCount: asNumber(pick(value, "staffCount", "staff_count") ?? pick(counts, "staff")),
    rewardCount: asNumber(
      pick(value, "rewardCount", "reward_count", "rewardsCount") ?? pick(counts, "rewards"),
    ),
    businessPanNumber: nullableString(pick(value, "businessPanNumber", "business_pan_number")),
    applicationStatus: (() => {
      const status = asString(pick(value, "applicationStatus", "application_status"), "APPROVED");
      return status === "PENDING_REVIEW" || status === "REJECTED" ? status : "APPROVED";
    })(),
    applicationSubmittedAt: nullableString(
      pick(value, "applicationSubmittedAt", "application_submitted_at"),
    ),
    applicationReviewedAt: nullableString(
      pick(value, "applicationReviewedAt", "application_reviewed_at"),
    ),
    applicationReviewNote: nullableString(
      pick(value, "applicationReviewNote", "application_review_note"),
    ),
    ownerEmailVerified: Boolean(
      nullableString(pick(value, "ownerEmailVerifiedAt", "owner_email_verified_at")),
    ),
    setupStatus:
      asString(pick(value, "setupStatus", "setup_status"), "LIVE") === "SETUP_REQUIRED"
        ? "SETUP_REQUIRED"
        : "LIVE",
    setupCompletedAt: nullableString(pick(value, "setupCompletedAt", "setup_completed_at")),
  };
}

function normalizeRedemption(value: unknown, index: number): Redemption | null {
  if (!isRecord(value)) return null;
  const reward = isRecord(value.reward) ? value.reward : {};
  const tenant = isRecord(value.tenant) ? value.tenant : {};
  return {
    publicId: asString(pick(value, "publicId", "public_id", "id"), `redemption-${String(index)}`),
    rewardTitle: asString(
      pick(value, "rewardTitle", "reward_title") ?? pick(reward, "title", "name"),
      "Reward",
    ),
    tenantName: asString(
      pick(value, "tenantName", "tenant_name") ??
        pick(tenant, "businessName", "business_name", "name"),
      "Mero Perks partner",
    ),
    pointsSpent: asNumber(pick(value, "pointsSpent", "points_spent", "pointsCost")),
    code: nullableString(pick(value, "code", "redemptionCode", "redemption_code")),
    qrPayload: nullableString(pick(value, "qrPayload", "qr_payload")),
    manualCode: nullableString(pick(value, "manualCode", "manual_code")),
    qrExpiresAt: nullableString(pick(value, "qrExpiresAt", "qr_expires_at")),
    status: asString(pick(value, "status"), "issued"),
    createdAt: nullableString(pick(value, "createdAt", "created_at", "redeemedAt")),
    fulfilledAt: nullableString(pick(value, "fulfilledAt", "fulfilled_at", "redeemedAt")),
  };
}

function normalizeCustomerAccount(value: unknown): CustomerAccount | null {
  if (!isRecord(value) || !isRecord(value.tenant) || !isRecord(value.program)) return null;
  const tenantName = asString(pick(value.tenant, "businessName", "business_name", "name"));
  const programName = asString(pick(value.program, "name"));
  if (!tenantName || !programName) return null;
  const redemption = isRecord(value.redemption) ? value.redemption : null;
  return {
    tenant: {
      publicId: asString(pick(value.tenant, "publicId", "public_id", "id")),
      slug: asString(pick(value.tenant, "slug")),
      businessName: tenantName,
      logoUrl: nullableString(pick(value.tenant, "logoUrl", "logo_url")),
    },
    program: {
      publicId: asString(pick(value.program, "publicId", "public_id", "id")),
      name: programName,
    },
    pointsBalance: asNumber(pick(value, "pointsBalance", "points_balance")),
    redemption: redemption
      ? {
          rulePublicId: asString(pick(redemption, "rulePublicId", "rule_public_id")),
          pointsPerUnit: asNumber(pick(redemption, "pointsPerUnit", "points_per_unit")),
          valuePerUnitMinor: asNumber(
            pick(redemption, "valuePerUnitMinor", "value_per_unit_minor"),
          ),
          minimumRedemptionPoints: asNumber(
            pick(redemption, "minimumRedemptionPoints", "minimum_redemption_points"),
          ),
          maximumRedemptionPointsPerTransaction: nullableNumber(
            pick(
              redemption,
              "maximumRedemptionPointsPerTransaction",
              "maximum_redemption_points_per_transaction",
            ),
          ),
          redeemablePoints: asNumber(pick(redemption, "redeemablePoints", "redeemable_points")),
          redeemableValueMinor: asNumber(
            pick(redemption, "redeemableValueMinor", "redeemable_value_minor"),
          ),
        }
      : null,
  };
}

function normalizeCustomerMembership(value: unknown): CustomerMembership | null {
  const account = normalizeCustomerAccount(value);
  if (!account || !isRecord(value)) return null;
  const tenant = isRecord(value.tenant) ? value.tenant : {};
  const preferences = isRecord(value.preferences) ? value.preferences : {};
  return {
    ...account,
    membershipPublicId: asString(pick(value, "membershipPublicId", "membership_public_id")),
    tenant: {
      ...account.tenant,
      primaryColor: asString(pick(tenant, "primaryColor", "primary_color"), "#C8102E"),
    },
    joinedAt: asString(pick(value, "joinedAt", "joined_at")),
    lastVisitAt: nullableString(pick(value, "lastVisitAt", "last_visit_at")),
    expiringPoints: asNumber(pick(value, "expiringPoints", "expiring_points")),
    nextPointsExpiry: nullableString(pick(value, "nextPointsExpiry", "next_points_expiry")),
    availableRewards: asNumber(pick(value, "availableRewards", "available_rewards")),
    preferences: {
      inApp: asBoolean(pick(preferences, "inApp", "in_app"), true),
      email: asBoolean(pick(preferences, "email")),
      push: asBoolean(pick(preferences, "push")),
      sms: asBoolean(pick(preferences, "sms")),
    },
  };
}

function normalizeCustomerNotification(value: unknown): CustomerNotification | null {
  if (!isRecord(value) || !isRecord(value.tenant)) return null;
  const publicId = asString(pick(value, "publicId", "public_id", "id"));
  const kind = asString(pick(value, "kind"));
  const title = asString(pick(value, "title"));
  const message = asString(pick(value, "message"));
  const expiresAt = asString(pick(value, "expiresAt", "expires_at"));
  const createdAt = asString(pick(value, "createdAt", "created_at"));
  const tenantPublicId = asString(pick(value.tenant, "publicId", "public_id", "id"));
  const tenantName = asString(pick(value.tenant, "name", "businessName", "business_name"));
  if (
    !publicId ||
    (kind !== "REWARD_EXPIRING" && kind !== "POINTS_EXPIRING") ||
    !title ||
    !message ||
    !expiresAt ||
    !createdAt ||
    !tenantPublicId ||
    !tenantName
  )
    return null;
  const actionPath = nullableString(pick(value, "actionPath", "action_path"));
  return {
    publicId,
    kind,
    title,
    message,
    actionPath: actionPath?.startsWith("/") ? actionPath : null,
    expiresAt,
    payload: value.payload ?? null,
    createdAt,
    tenant: { publicId: tenantPublicId, name: tenantName },
  };
}

export async function fetchPublicRewards(): Promise<Reward[]> {
  const rewards: Reward[] = [];
  const pageSize = 50;
  for (let offset = 0; offset < 10_000; offset += pageSize) {
    const payload = await request(
      `/api/v1/public/rewards?limit=${String(pageSize)}&offset=${String(offset)}`,
    );
    const items = getItems(payload, "rewards");
    rewards.push(
      ...items.map(normalizeReward).filter((reward): reward is Reward => reward !== null),
    );
    if (items.length < pageSize) break;
  }
  return rewards;
}

export async function fetchPublicReward(publicId: string): Promise<Reward> {
  const payload = await request(`/api/v1/public/rewards/${encodeURIComponent(publicId)}`);
  const data = unwrap(payload);
  const reward = normalizeReward(isRecord(data) ? (data.reward ?? data) : data, 0);
  if (!reward) throw new ApiError("The selected reward could not be read.", 500);
  return reward;
}

export async function fetchSession(): Promise<SessionUser | null> {
  try {
    const payload = await request("/api/v1/me");
    return normalizeSession(payload);
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export async function registerCustomer(input: RegisterInput): Promise<RegistrationResult> {
  const payload = await request("/api/v1/auth/customer/register", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const data = unwrap(payload);
  if (!isRecord(data)) throw new ApiError("Registration could not be completed.", 500);
  const email = asString(data.email);
  const delivery = asString(data.delivery);
  if (
    !email ||
    (delivery !== "sent" &&
      delivery !== "already_verified" &&
      delivery !== "temporarily_unavailable")
  ) {
    throw new ApiError("Registration could not be completed.", 500);
  }
  return {
    email,
    verificationRequired: asBoolean(data.verificationRequired, true),
    delivery,
  };
}

export async function registerTenant(
  input: TenantRegistrationInput,
): Promise<TenantRegistrationResult> {
  const payload = await request("/api/v1/auth/tenant/register", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const data = unwrap(payload);
  if (!isRecord(data)) throw new ApiError("Business application could not be submitted.", 500);
  const delivery = asString(data.delivery);
  if (
    !["sent", "already_verified", "temporarily_unavailable"].includes(delivery) ||
    !asString(data.email) ||
    !asString(data.tenantPublicId) ||
    !asString(data.tenantSlug)
  ) {
    throw new ApiError("Business application could not be submitted.", 500);
  }
  return {
    tenantPublicId: asString(data.tenantPublicId),
    tenantSlug: asString(data.tenantSlug),
    email: asString(data.email),
    verificationRequired: asBoolean(data.verificationRequired, true),
    delivery: delivery as VerificationDelivery,
    applicationStatus: "PENDING_REVIEW",
    subscriptionPlan: (asString(data.subscriptionPlan) ||
      input.subscriptionPlan) as TenantRegistrationResult["subscriptionPlan"],
    billingCycle: (asString(data.billingCycle) ||
      input.billingCycle) as TenantRegistrationResult["billingCycle"],
  };
}

export async function checkBusinessPanAvailability(
  businessPanNumber: string,
): Promise<BusinessPanAvailability> {
  const payload = await request("/api/v1/auth/tenant/pan-availability", {
    method: "POST",
    body: JSON.stringify({ businessPanNumber }),
  });
  const data = unwrap(payload);
  if (!isRecord(data)) throw new ApiError("PAN availability could not be checked.", 500);
  const checkedPan = asString(data.businessPanNumber);
  if (!checkedPan) throw new ApiError("PAN availability could not be checked.", 500);
  return {
    businessPanNumber: checkedPan,
    available: asBoolean(data.available, false),
    message: asString(data.message, "PAN availability could not be checked."),
  };
}

export async function verifyEmail(token: string): Promise<void> {
  await request("/api/v1/auth/email/verify", {
    method: "POST",
    body: JSON.stringify({ token }),
  });
}

export async function resendVerificationEmail(input: {
  email: string;
  portal: "customer" | "tenant";
}): Promise<void> {
  await request("/api/v1/auth/email/resend", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function resetPassword(token: string, password: string): Promise<void> {
  await request("/api/v1/auth/password/reset", {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });
}

export async function login(input: LoginInput): Promise<SessionUser | null> {
  const endpoint =
    input.portal === "customer"
      ? "/api/v1/auth/customer/login"
      : input.portal === "tenant"
        ? "/api/v1/auth/tenant/login"
        : "/api/v1/auth/super-admin/login";
  const body: Record<string, string> = { email: input.email, password: input.password };
  if (input.portal === "tenant" && input.tenantSlug) body.tenantSlug = input.tenantSlug;
  const payload = await request(endpoint, { method: "POST", body: JSON.stringify(body) });
  const expectedType: SessionType =
    input.portal === "super-admin"
      ? "super_admin"
      : input.portal === "tenant"
        ? "tenant_staff"
        : "customer";
  return normalizeSession(payload, expectedType);
}

export async function bootstrapSuperAdmin(
  input: BootstrapSuperAdminInput,
): Promise<SessionUser | null> {
  const { bootstrapSecret, ...profile } = input;
  const payload = await request("/api/v1/auth/super-admin/bootstrap", {
    method: "POST",
    headers: { "x-bootstrap-secret": bootstrapSecret },
    body: JSON.stringify(profile),
  });
  return normalizeSession(payload, "super_admin");
}

export async function logout(): Promise<void> {
  await request("/api/v1/auth/logout", { method: "POST" });
}

export async function redeemReward(
  rewardPublicId: string,
  mechanismSelectionId?: string,
): Promise<Redemption> {
  const payload = await request(
    `/api/v1/customer/rewards/${encodeURIComponent(rewardPublicId)}/redemptions`,
    {
      method: "POST",
      body: JSON.stringify({ idempotencyKey: crypto.randomUUID(), mechanismSelectionId }),
    },
  );
  const data = unwrap(payload);
  const redemption = normalizeRedemption(isRecord(data) ? (data.redemption ?? data) : data, 0);
  if (!redemption)
    throw new ApiError("The reward was redeemed, but its details could not be read.", 500);
  return redemption;
}

export async function fetchRedemptions(): Promise<Redemption[]> {
  const payload = await request("/api/v1/customer/redemptions");
  return getItems(payload, "redemptions")
    .map(normalizeRedemption)
    .filter((item): item is Redemption => item !== null);
}

export async function fetchCustomerAccounts(): Promise<CustomerAccount[]> {
  const payload = await request("/api/v1/customer/accounts");
  return getItems(payload, "accounts")
    .map(normalizeCustomerAccount)
    .filter((account): account is CustomerAccount => account !== null);
}

export async function fetchCustomerNotifications(): Promise<CustomerNotification[]> {
  const payload = await request("/api/v1/customer/notifications");
  return getItems(payload, "notifications")
    .map(normalizeCustomerNotification)
    .filter((notification): notification is CustomerNotification => notification !== null);
}

export async function fetchCustomerHome(): Promise<CustomerHomeData> {
  const data = dataRecord(await request("/api/v1/customer/experience/home"));
  const tierSummary = isRecord(data.tierSummary)
    ? (data.tierSummary as unknown as TierStatus)
    : null;
  const profile = isRecord(data.profile)
    ? (data.profile as unknown as CustomerProfileDetail)
    : null;
  if (!tierSummary || !profile) throw new ApiError("Your rewards home could not be read.", 500);
  return {
    profile,
    tierSummary,
    urgentItems: Array.isArray(data.urgentItems)
      ? data.urgentItems
          .map(normalizeCustomerNotification)
          .filter((item): item is CustomerNotification => item !== null)
      : [],
    businessWalletSummaries: Array.isArray(data.businessWalletSummaries)
      ? data.businessWalletSummaries
          .map(normalizeCustomerMembership)
          .filter((item): item is CustomerMembership => item !== null)
      : [],
    recommendedOffers: Array.isArray(data.recommendedOffers)
      ? data.recommendedOffers.map(normalizeReward).filter((item): item is Reward => item !== null)
      : [],
    recentActivity: Array.isArray(data.recentActivity)
      ? (data.recentActivity as CustomerActivityItem[])
      : [],
    pendingActions: Array.isArray(data.pendingActions)
      ? (data.pendingActions as CustomerHomeData["pendingActions"])
      : [],
    notificationsSummary: isRecord(data.notificationsSummary)
      ? (data.notificationsSummary as unknown as CustomerHomeData["notificationsSummary"])
      : { unread: 0, total: 0 },
  };
}

export async function fetchCustomerWallet(): Promise<CustomerWalletData> {
  const data = dataRecord(await request("/api/v1/customer/experience/wallet"));
  return {
    memberships: Array.isArray(data.memberships)
      ? data.memberships
          .map(normalizeCustomerMembership)
          .filter((item): item is CustomerMembership => item !== null)
      : [],
    entitlements: Array.isArray(data.entitlements)
      ? (data.entitlements as CustomerEntitlement[])
      : [],
    redemptions: Array.isArray(data.redemptions)
      ? data.redemptions
          .map(normalizeRedemption)
          .filter((item): item is Redemption => item !== null)
      : [],
  };
}

export async function fetchCustomerPunchCards(): Promise<CustomerPunchCardProgress[]> {
  const data = dataRecord(await request("/api/v1/customer/punch-cards"));
  return Array.isArray(data.punchCards) ? (data.punchCards as CustomerPunchCardProgress[]) : [];
}

export async function fetchCustomerRewardCoupon(publicId: string): Promise<Redemption> {
  const data = dataRecord(
    await request(`/api/v1/customer/experience/redemptions/${encodeURIComponent(publicId)}/coupon`),
  );
  const redemption = normalizeRedemption(data.redemption, 0);
  if (!redemption) throw new ApiError("The coupon could not be read.", 500);
  return redemption;
}

export async function fetchCustomerActivity(): Promise<CustomerActivityItem[]> {
  const data = dataRecord(await request("/api/v1/customer/experience/activity"));
  return Array.isArray(data.activity) ? (data.activity as CustomerActivityItem[]) : [];
}

export async function fetchCustomerProfileDetail(): Promise<CustomerProfileDetail> {
  const data = dataRecord(await request("/api/v1/customer/experience/profile"));
  if (!isRecord(data.profile)) throw new ApiError("Your profile could not be read.", 500);
  return data.profile as unknown as CustomerProfileDetail;
}

export async function updateCustomerProfileDetail(
  input: CustomerProfileUpdateInput,
): Promise<CustomerProfileUpdateResult> {
  const data = dataRecord(
    await request("/api/v1/customer/experience/profile", {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  );
  if (!isRecord(data.profile)) throw new ApiError("Your profile could not be updated.", 500);
  return {
    profile: data.profile as unknown as CustomerProfileDetail,
    emailChanged: data.emailChanged === true,
    verificationRequired: data.verificationRequired === true,
    delivery: typeof data.delivery === "string" ? data.delivery : null,
  };
}

export async function fetchBusinessOwnerAccount(): Promise<BusinessOwnerAccountDetail> {
  const data = dataRecord(await request("/api/v1/tenant/experience/account"));
  if (!isRecord(data.account))
    throw new ApiError("Your Business Owner account could not be read.", 500);
  return data.account as unknown as BusinessOwnerAccountDetail;
}

export async function fetchBusinessSetupStatus(): Promise<BusinessSetupStatus> {
  const data = dataRecord(await request("/api/v1/tenant/experience/setup"));
  if (!isRecord(data.setup)) throw new ApiError("Business setup status could not be read.", 500);
  return data.setup as unknown as BusinessSetupStatus;
}

export async function fetchBusinessSubscription(): Promise<BusinessSubscription> {
  const data = dataRecord(await request("/api/v1/tenant/subscription"));
  if (!isRecord(data.subscription))
    throw new ApiError("Business subscription could not be read.", 500);
  return data.subscription as unknown as BusinessSubscription;
}

export async function changeBusinessSubscription(input: {
  planCode: SubscriptionPlanCode;
  billingCycle: SubscriptionBillingCycle;
  paymentMethod: SubscriptionPaymentMethod;
}): Promise<BusinessSubscriptionChangeResult> {
  const data = dataRecord(
    await request("/api/v1/tenant/subscription/change-plan", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  );
  if (!isRecord(data.subscription) || !isRecord(data.payment)) {
    throw new ApiError("The subscription change response was incomplete.", 500);
  }
  return data as unknown as BusinessSubscriptionChangeResult;
}

export async function updateBusinessOwnerAccount(
  input: Pick<BusinessOwnerAccountDetail, "firstName" | "lastName" | "phone" | "profileImageUrl">,
): Promise<BusinessOwnerAccountDetail> {
  const data = dataRecord(
    await request("/api/v1/tenant/experience/account", {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  );
  if (!isRecord(data.account))
    throw new ApiError("Your Business Owner account could not be updated.", 500);
  return data.account as unknown as BusinessOwnerAccountDetail;
}

export async function fetchCustomerPreferences(): Promise<CustomerPreferences> {
  const data = dataRecord(await request("/api/v1/customer/experience/preferences"));
  if (!isRecord(data.preferences)) throw new ApiError("Your preferences could not be read.", 500);
  return data.preferences as unknown as CustomerPreferences;
}

export async function updateCustomerPreferences(
  input: CustomerPreferences,
): Promise<CustomerPreferences> {
  const data = dataRecord(
    await request("/api/v1/customer/experience/preferences", {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  );
  if (!isRecord(data.preferences)) throw new ApiError("Your preferences could not be read.", 500);
  return data.preferences as unknown as CustomerPreferences;
}

export async function recordCustomerAttribution(input: {
  tenantSlug?: string;
  campaignCode?: string;
  referralCode?: string;
  originUrl?: string;
}): Promise<void> {
  await request("/api/v1/customer/experience/attribution", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function joinCustomerBusiness(tenantSlug: string): Promise<CustomerMembership> {
  const data = dataRecord(
    await request(`/api/v1/customer/experience/businesses/${encodeURIComponent(tenantSlug)}/join`, {
      method: "POST",
    }),
  );
  const membership = normalizeCustomerMembership(data.membership);
  if (!membership) throw new ApiError("Your business membership could not be read.", 500);
  return membership;
}

export async function prepareCustomerRedemption(
  rewardPublicId: string,
  mechanismSelectionId?: string,
) {
  const data = dataRecord(
    await request("/api/v1/customer/experience/redemptions/prepare", {
      method: "POST",
      body: JSON.stringify({
        rewardPublicId,
        mechanismSelectionId,
        idempotencyKey: crypto.randomUUID(),
      }),
    }),
  );
  if (!isRecord(data.preparation))
    throw new ApiError("The redemption confirmation could not be prepared.", 500);
  return data.preparation as unknown as {
    publicId: string;
    token: string;
    status: string;
    expiresAt: string;
  };
}

export async function claimPreparedCustomerRedemption(token: string): Promise<Redemption> {
  const data = dataRecord(
    await request("/api/v1/customer/experience/redemptions/claim", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
  );
  const redemption = normalizeRedemption(data.redemption, 0);
  if (!redemption) throw new ApiError("The redemption could not be read.", 500);
  return redemption;
}

export async function createCustomerSupportCase(input: {
  tenantSlug?: string;
  category:
    "MISSING_POINTS" | "REDEMPTION" | "TRANSACTION" | "RECEIPT" | "ACCOUNT" | "PRIVACY" | "OTHER";
  subject: string;
  message: string;
  relatedPublicId?: string;
}) {
  const data = dataRecord(
    await request("/api/v1/customer/experience/support/cases", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  );
  if (!isRecord(data.supportCase)) throw new ApiError("Your support case could not be read.", 500);
  return data.supportCase as unknown as { publicId: string; status: string; createdAt: string };
}

export async function fetchMarketplaceBusinesses(
  input: {
    search?: string;
    types?: string[];
    latitude?: number;
    longitude?: number;
    radiusKm?: number;
  } = {},
): Promise<{ items: MarketplaceBusiness[]; total: number }> {
  const query = new URLSearchParams({ limit: "100", offset: "0" });
  if (input.search) query.set("search", input.search);
  if (input.types?.length) query.set("types", input.types.join(","));
  if (input.latitude !== undefined && input.longitude !== undefined) {
    query.set("latitude", String(input.latitude));
    query.set("longitude", String(input.longitude));
  }
  if (input.radiusKm) query.set("radiusKm", String(input.radiusKm));
  const data = dataRecord(
    await request(`/api/v1/public/marketplace/businesses?${query.toString()}`),
  );
  return {
    items: Array.isArray(data.items) ? (data.items as MarketplaceBusiness[]) : [],
    total: asNumber(data.total),
  };
}

export async function fetchMarketplaceBusinessTypes(): Promise<
  Array<{ value: string; count: number }>
> {
  const data = dataRecord(await request("/api/v1/public/marketplace/business-types"));
  return Array.isArray(data.items) ? (data.items as Array<{ value: string; count: number }>) : [];
}

export async function fetchMarketplaceBusiness(
  slug: string,
  coordinates?: { latitude: number; longitude: number },
): Promise<MarketplaceBusiness> {
  const query = new URLSearchParams();
  if (coordinates) {
    query.set("latitude", String(coordinates.latitude));
    query.set("longitude", String(coordinates.longitude));
  }
  const data = dataRecord(
    await request(
      `/api/v1/public/marketplace/businesses/${encodeURIComponent(slug)}${query.size ? `?${query.toString()}` : ""}`,
    ),
  );
  if (!isRecord(data.business)) throw new ApiError("Business details could not be read.", 500);
  return data.business as unknown as MarketplaceBusiness;
}

export async function fetchDigitalMemberCard(): Promise<DigitalMemberCard> {
  const data = dataRecord(await request("/api/v1/customer/experience/digital-card"));
  if (!isRecord(data.card)) throw new ApiError("Your digital card could not be read.", 500);
  return data.card as unknown as DigitalMemberCard;
}

export async function reissueDigitalMemberCard(): Promise<DigitalMemberCard> {
  const data = dataRecord(
    await request("/api/v1/customer/experience/digital-card/reissue", { method: "POST" }),
  );
  if (!isRecord(data.card)) throw new ApiError("Your digital card could not be reissued.", 500);
  return data.card as unknown as DigitalMemberCard;
}

export async function fetchTenantBusinessLocations(): Promise<TenantBusinessLocation[]> {
  const data = dataRecord(await request("/api/v1/tenant/experience/locations"));
  return Array.isArray(data.locations) ? (data.locations as TenantBusinessLocation[]) : [];
}

export async function geocodeBusinessLocation(query: string): Promise<GeocodeCandidate[]> {
  const data = dataRecord(
    await request("/api/v1/tenant/experience/locations/geocode", {
      method: "POST",
      body: JSON.stringify({ query }),
    }),
  );
  return Array.isArray(data.candidates) ? (data.candidates as GeocodeCandidate[]) : [];
}

export async function saveTenantBusinessLocation(
  input: BusinessLocationInput,
  publicId?: string,
): Promise<TenantBusinessLocation> {
  const data = dataRecord(
    await request(
      publicId
        ? `/api/v1/tenant/experience/locations/${encodeURIComponent(publicId)}`
        : "/api/v1/tenant/experience/locations",
      {
        method: publicId ? "PUT" : "POST",
        body: JSON.stringify(input),
      },
    ),
  );
  if (!isRecord(data.location))
    throw new ApiError("The location could not be read after saving.", 500);
  return data.location as unknown as TenantBusinessLocation;
}

export async function resolveMemberScan(payload: string) {
  const data = dataRecord(
    await request("/api/v1/tenant/experience/scanner/resolve", {
      method: "POST",
      body: JSON.stringify({ payload }),
    }),
  );
  if (!isRecord(data.scan)) throw new ApiError("The member code could not be resolved.", 500);
  return data.scan as unknown as {
    publicId: string;
    sessionToken: string;
    expiresAt: string;
    customer: {
      publicId: string;
      displayName: string;
      memberNumber: string;
      tierCode: string;
      tierName: string;
      profileImageUrl: string | null;
    };
  };
}

export async function confirmMemberScan(token: string) {
  const data = dataRecord(
    await request("/api/v1/tenant/experience/scanner/validate", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),
  );
  if (!isRecord(data.scan)) throw new ApiError("The member scan could not be confirmed.", 500);
  return data.scan as unknown as {
    publicId: string;
    status: string;
    customer: {
      publicId: string;
      displayName: string;
      profileImageUrl: string | null;
      memberNumber: string;
      tierCode: string;
      tierName: string;
    };
    message: string;
  };
}

export async function fetchTenants(): Promise<Tenant[]> {
  const payload = await request("/api/v1/super-admin/tenants");
  return getItems(payload, "tenants")
    .map(normalizeTenant)
    .filter((tenant): tenant is Tenant => tenant !== null);
}

export async function createTenant(input: CreateTenantInput): Promise<Tenant | null> {
  const payload = await request("/api/v1/super-admin/tenants", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const data = unwrap(payload);
  return normalizeTenant(isRecord(data) ? (data.tenant ?? data) : data, 0);
}

export async function updatePlatformTenant(
  publicId: string,
  input: Partial<{
    slug: string;
    businessName: string;
    legalName: string | null;
    businessType: string;
    businessEmail: string;
    businessPhone: string | null;
    website: string | null;
    defaultCurrency: string;
    defaultTimezone: string;
    country: string;
    address: string | null;
    city: string | null;
    stateProvince: string | null;
    postalCode: string | null;
    logoUrl: string | null;
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    planId: string | null;
  }>,
): Promise<Tenant> {
  const data = dataRecord(
    await request(`/api/v1/super-admin/tenants/${encodeURIComponent(publicId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  );
  const tenant = normalizeTenant(data.tenant, 0);
  if (!tenant) throw new ApiError("The updated tenant could not be read.", 500);
  return tenant;
}

export async function sendTenantOwnerPasswordReset(publicId: string) {
  return dataRecord(
    await request(`/api/v1/super-admin/tenants/${encodeURIComponent(publicId)}/password-reset`, {
      method: "POST",
    }),
  ) as unknown as { delivery: "sent" | "temporarily_unavailable"; email: string };
}

export async function archivePlatformTenant(
  publicId: string,
  confirmation: string,
): Promise<ArchivedPlatformAccount> {
  const data = dataRecord(
    await request(`/api/v1/super-admin/tenants/${encodeURIComponent(publicId)}/archive`, {
      method: "POST",
      body: JSON.stringify({ confirmation }),
    }),
  );
  if (!isRecord(data.archive)) {
    throw new ApiError("The archived business result could not be read.", 500);
  }
  return data.archive as unknown as ArchivedPlatformAccount;
}

export async function fetchTenantRewards(): Promise<Reward[]> {
  const payload = await request("/api/v1/tenant/rewards");
  return getItems(payload, "rewards")
    .map(normalizeReward)
    .filter((reward): reward is Reward => reward !== null);
}

export async function fetchTenantInventory(): Promise<InventoryItem[]> {
  const data = dataRecord(await request("/api/v1/tenant/rewards/inventory"));
  return Array.isArray(data.items) ? (data.items as InventoryItem[]) : [];
}

export async function saveTenantInventoryItem(
  input: {
    code: string;
    name: string;
    brand?: string;
    category: string;
    priceMinor?: number;
    stockQuantity?: number;
    imageUrl?: string;
    status: "active" | "inactive";
  },
  publicId?: string,
): Promise<InventoryItem> {
  const data = dataRecord(
    await request(
      publicId
        ? `/api/v1/tenant/rewards/inventory/${encodeURIComponent(publicId)}`
        : "/api/v1/tenant/rewards/inventory",
      {
        method: publicId ? "PATCH" : "POST",
        body: JSON.stringify(publicId ? { ...input, code: undefined } : input),
      },
    ),
  );
  if (!isRecord(data.item))
    throw new ApiError("The inventory item could not be read after saving.", 500);
  return data.item as unknown as InventoryItem;
}

export async function createTenantReward(input: TenantRewardInput): Promise<Reward | null> {
  const payload = await request("/api/v1/tenant/rewards", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const data = unwrap(payload);
  return normalizeReward(isRecord(data) ? (data.reward ?? data) : data, 0);
}

export async function updateTenantReward(
  publicId: string,
  input: Partial<TenantRewardInput>,
): Promise<Reward | null> {
  const payload = await request(`/api/v1/tenant/rewards/${encodeURIComponent(publicId)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  const data = unwrap(payload);
  return normalizeReward(isRecord(data) ? (data.reward ?? data) : data, 0);
}

export async function fetchTenantRewardDashboard(): Promise<TenantRewardDashboard> {
  const payload = await request("/api/v1/tenant/rewards/dashboard");
  const data = unwrap(payload);
  const root = isRecord(data) ? data : {};
  const summary = isRecord(root.summary) ? root.summary : {};
  const normalizeCountGroup = (value: unknown, key: "status" | "type") =>
    Array.isArray(value)
      ? value.flatMap((item) => {
          if (!isRecord(item)) return [];
          const label = asString(
            pick(item, key, key === "status" ? "lifecycleStatus" : "rewardType"),
          );
          return label ? [{ [key]: label, count: asNumber(pick(item, "count")) }] : [];
        })
      : [];
  const recentRedemptions = Array.isArray(root.recentRedemptions)
    ? root.recentRedemptions.flatMap((item, index) => {
        if (!isRecord(item)) return [];
        return [
          {
            publicId: asString(
              pick(item, "publicId", "public_id", "id"),
              `recent-${String(index)}`,
            ),
            rewardTitle: asString(
              pick(
                item,
                "rewardTitle",
                "reward_title",
                "rewardName",
                "reward_name",
                "title",
                "name",
              ),
              "Reward",
            ),
            customerName: asString(
              pick(item, "customerName", "customer_name", "memberName"),
              "Member",
            ),
            locationName: nullableString(pick(item, "locationName", "location_name")),
            valueMinor: asNumber(
              pick(item, "valueMinor", "value_minor", "rewardValueMinor", "reward_value_minor"),
            ),
            currencyCode: asString(pick(item, "currencyCode", "currency_code"), "NPR"),
            redeemedAt: asString(
              pick(item, "redeemedAt", "redeemed_at", "createdAt", "created_at"),
            ),
          },
        ];
      })
    : [];
  return {
    summary: {
      totalRewards: asNumber(pick(summary, "totalRewards", "total_rewards")),
      activeRewards: asNumber(pick(summary, "activeRewards", "active_rewards")),
      draftRewards: asNumber(pick(summary, "draftRewards", "draft_rewards")),
      scheduledRewards: asNumber(pick(summary, "scheduledRewards", "scheduled_rewards")),
      pendingApproval: asNumber(
        pick(
          summary,
          "pendingApproval",
          "pendingApprovals",
          "pending_approval",
          "pending_approvals",
        ),
      ),
      issuedCount: asNumber(
        pick(summary, "issuedCount", "issued_count", "totalIssued", "total_issued"),
      ),
      redeemedCount: asNumber(
        pick(summary, "redeemedCount", "redeemed_count", "totalRedeemed", "total_redeemed"),
      ),
      redemptionRate: asNumber(pick(summary, "redemptionRate", "redemption_rate")),
      configuredBudgetMinor: asNumber(
        pick(summary, "configuredBudgetMinor", "configured_budget_minor"),
      ),
      committedMinor: asNumber(pick(summary, "committedMinor", "committed_minor")),
      redeemedValueMinor: asNumber(pick(summary, "redeemedValueMinor", "redeemed_value_minor")),
      outstandingBusinessPoints: asNumber(
        pick(summary, "outstandingBusinessPoints", "outstanding_business_points"),
      ),
      estimatedLiabilityMinor: asNumber(
        pick(
          summary,
          "estimatedLiabilityMinor",
          "estimated_liability_minor",
          "committedMinor",
          "committed_minor",
          "rewardCostMinor",
          "reward_cost_minor",
        ),
      ),
      pointLiability: asNumber(
        pick(
          summary,
          "pointLiability",
          "point_liability",
          "outstandingBusinessPoints",
          "outstanding_business_points",
          "pointsLiability",
          "points_liability",
        ),
      ),
      totalRewardExposureMinor: asNumber(
        pick(summary, "totalRewardExposureMinor", "total_reward_exposure_minor"),
      ),
    },
    byLifecycle: normalizeCountGroup(root.byLifecycle ?? root.by_lifecycle, "status") as Array<{
      status: string;
      count: number;
    }>,
    byType: normalizeCountGroup(root.byType ?? root.by_type, "type") as Array<{
      type: string;
      count: number;
    }>,
    recentRedemptions,
  };
}

function normalizeTenantRewardAnalyticsMetric(value: unknown): TenantRewardAnalyticsMetric {
  const metric = isRecord(value) ? value : {};
  const opportunityCount = asNumber(pick(metric, "opportunityCount", "opportunity_count"));
  const redeemedCount = asNumber(pick(metric, "redeemedCount", "redeemed_count"));
  const rawRate = pick(metric, "redemptionRate", "redemption_rate");
  return {
    opportunityCount,
    issuedCount: asNumber(pick(metric, "issuedCount", "issued_count")),
    claimedCount: asNumber(pick(metric, "claimedCount", "claimed_count")),
    redeemedCount,
    expiredCount: asNumber(pick(metric, "expiredCount", "expired_count")),
    cancelledCount: asNumber(pick(metric, "cancelledCount", "cancelled_count")),
    reversedCount: asNumber(pick(metric, "reversedCount", "reversed_count")),
    rewardCostMinor: asNumber(pick(metric, "rewardCostMinor", "reward_cost_minor")),
    associatedRevenueMinor: asNumber(
      pick(metric, "associatedRevenueMinor", "associated_revenue_minor"),
    ),
    redemptionRate:
      rawRate === undefined
        ? opportunityCount > 0
          ? redeemedCount / opportunityCount
          : 0
        : asNumber(rawRate),
  };
}

function normalizeTenantRewardAnalyticsBreakdown(value: unknown): TenantRewardAnalyticsBreakdown[] {
  return Array.isArray(value)
    ? value.flatMap((item, index) => {
        if (!isRecord(item)) return [];
        const name = asString(pick(item, "name"));
        if (!name) return [];
        return [
          {
            publicId: asString(
              pick(item, "publicId", "public_id", "id"),
              `analytics-${String(index)}`,
            ),
            name,
            ...normalizeTenantRewardAnalyticsMetric(item),
          },
        ];
      })
    : [];
}

function normalizeTenantRewardTierAnalyticsBreakdown(
  value: unknown,
): TenantRewardTierAnalyticsBreakdown[] {
  return Array.isArray(value)
    ? value.flatMap((item) => {
        if (!isRecord(item)) return [];
        const code = asString(pick(item, "code"));
        const name = asString(pick(item, "name"), code);
        if (!code || !name) return [];
        return [{ code, name, ...normalizeTenantRewardAnalyticsMetric(item) }];
      })
    : [];
}

export async function fetchTenantRewardAnalytics(input: {
  from: string;
  to: string;
}): Promise<TenantRewardAnalyticsReport> {
  const query = new URLSearchParams({ from: input.from, to: input.to });
  const payload = await request(`/api/v1/tenant/rewards/analytics?${query.toString()}`);
  const data = unwrap(payload);
  const root = isRecord(data) ? data : {};
  const window = isRecord(root.window) ? root.window : {};
  const definitions = isRecord(root.definitions) ? root.definitions : {};
  return {
    window: {
      from: asString(pick(window, "from"), input.from),
      to: asString(pick(window, "to"), input.to),
    },
    generatedAt: asString(pick(root, "generatedAt", "generated_at")),
    summary: normalizeTenantRewardAnalyticsMetric(root.summary),
    byReward: normalizeTenantRewardAnalyticsBreakdown(root.byReward ?? root.by_reward),
    byCampaign: normalizeTenantRewardAnalyticsBreakdown(root.byCampaign ?? root.by_campaign),
    byLocation: normalizeTenantRewardAnalyticsBreakdown(root.byLocation ?? root.by_location),
    bySegment: normalizeTenantRewardAnalyticsBreakdown(root.bySegment ?? root.by_segment),
    byMeroTier: normalizeTenantRewardTierAnalyticsBreakdown(root.byMeroTier ?? root.by_mero_tier),
    definitions: {
      issued: asString(pick(definitions, "issued")),
      claimed: asString(pick(definitions, "claimed")),
      redeemed: asString(pick(definitions, "redeemed")),
      rewardCost: asString(pick(definitions, "rewardCost", "reward_cost")),
      associatedRevenue: asString(pick(definitions, "associatedRevenue", "associated_revenue")),
      tierAttribution: asString(pick(definitions, "tierAttribution", "tier_attribution")),
    },
  };
}

export async function fetchTenantRewardTemplates(): Promise<TenantRewardTemplate[]> {
  const payload = await request("/api/v1/tenant/rewards/templates");
  return getItems(payload, "templates").flatMap((item) => {
    if (!isRecord(item)) return [];
    const reward = isRecord(item.reward) ? (item.reward as Partial<TenantRewardInput>) : {};
    const code = asString(pick(item, "code"));
    const name = asString(pick(item, "name"));
    if (!code || !name) return [];
    return [
      {
        code,
        name,
        businessType: asString(pick(item, "businessType", "business_type"), "Retail"),
        description: asString(pick(item, "description")),
        reward,
      },
    ];
  });
}

export async function fetchTenantRewardCalendar(): Promise<TenantRewardCalendarItem[]> {
  const payload = await request("/api/v1/tenant/rewards/calendar");
  return getItems(payload, "items")
    .map(normalizeTenantRewardCalendarItem)
    .filter((item): item is TenantRewardCalendarItem => item !== null);
}

export async function fetchTenantRewardSegments(): Promise<TenantRewardSegment[]> {
  const payload = await request("/api/v1/tenant/rewards/segments");
  return getItems(payload, "segments")
    .map(normalizeTenantRewardSegment)
    .filter((segment): segment is TenantRewardSegment => segment !== null);
}

export async function createTenantRewardSegment(
  input: TenantRewardSegmentInput,
): Promise<TenantRewardSegment> {
  const payload = await request("/api/v1/tenant/rewards/segments", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const data = unwrap(payload);
  const segment = normalizeTenantRewardSegment(isRecord(data) ? (data.segment ?? data) : data);
  if (!segment)
    throw new ApiError("The segment was created, but its details could not be read.", 500);
  return segment;
}

export async function updateTenantRewardSegment(
  publicId: string,
  input: Partial<TenantRewardSegmentInput>,
): Promise<TenantRewardSegment> {
  const payload = await request(`/api/v1/tenant/rewards/segments/${encodeURIComponent(publicId)}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  const data = unwrap(payload);
  const segment = normalizeTenantRewardSegment(isRecord(data) ? (data.segment ?? data) : data);
  if (!segment)
    throw new ApiError("The segment was updated, but its details could not be read.", 500);
  return segment;
}

export async function archiveTenantRewardSegment(publicId: string): Promise<TenantRewardSegment> {
  const payload = await request(
    `/api/v1/tenant/rewards/segments/${encodeURIComponent(publicId)}/archive`,
    { method: "POST" },
  );
  const data = unwrap(payload);
  const segment = normalizeTenantRewardSegment(isRecord(data) ? (data.segment ?? data) : data);
  if (!segment)
    throw new ApiError("The segment was archived, but its details could not be read.", 500);
  return segment;
}

export async function fetchTenantRewardCampaigns(): Promise<TenantRewardCampaign[]> {
  const payload = await request("/api/v1/tenant/rewards/campaigns");
  return getItems(payload, "campaigns")
    .map(normalizeTenantRewardCampaign)
    .filter((campaign): campaign is TenantRewardCampaign => campaign !== null);
}

export async function createTenantRewardCampaign(
  input: TenantRewardCampaignInput,
): Promise<TenantRewardCampaign> {
  const payload = await request("/api/v1/tenant/rewards/campaigns", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const data = unwrap(payload);
  const campaign = normalizeTenantRewardCampaign(
    isRecord(data) ? (data.campaign ?? data) : data,
    0,
  );
  if (!campaign)
    throw new ApiError("The campaign was created, but its details could not be read.", 500);
  return campaign;
}

export async function updateTenantRewardCampaignExecutionLimits(
  publicId: string,
  input: TenantCampaignExecutionLimitsInput,
): Promise<TenantCampaignExecutionLimits> {
  const payload = await request(
    `/api/v1/tenant/rewards/campaigns/${encodeURIComponent(publicId)}/execution-limits`,
    { method: "PUT", body: JSON.stringify(input) },
  );
  const data = unwrap(payload);
  const campaign = isRecord(data) && isRecord(data.campaign) ? data.campaign : data;
  if (!isRecord(campaign)) {
    throw new ApiError("Campaign limits were updated, but the response could not be read.", 500);
  }
  const responsePublicId = asString(pick(campaign, "publicId", "public_id"));
  if (!responsePublicId) {
    throw new ApiError(
      "Campaign limits were updated, but the campaign identifier was missing.",
      500,
    );
  }
  return {
    publicId: responsePublicId,
    quantityLimit: nullableNumber(pick(campaign, "quantityLimit", "quantity_limit")),
    budgetMinor: nullableNumber(pick(campaign, "budgetMinor", "budget_minor")),
  };
}

export async function transitionTenantRewardCampaign(
  publicId: string,
  target: TenantRewardCampaignStatus,
): Promise<{ publicId: string; status: TenantRewardCampaignStatus }> {
  const payload = await request(
    `/api/v1/tenant/rewards/campaigns/${encodeURIComponent(publicId)}/transition`,
    { method: "POST", body: JSON.stringify({ target }) },
  );
  const data = unwrap(payload);
  const campaign = isRecord(data) && isRecord(data.campaign) ? data.campaign : data;
  if (!isRecord(campaign)) {
    throw new ApiError("The campaign transitioned, but the response could not be read.", 500);
  }
  const responsePublicId = asString(pick(campaign, "publicId", "public_id"));
  if (!responsePublicId) {
    throw new ApiError("The campaign transitioned, but its identifier was missing.", 500);
  }
  return {
    publicId: responsePublicId,
    status: normalizeTenantRewardCampaignStatus(pick(campaign, "status")),
  };
}

export async function fetchTenantRewardReferrals(
  query: TenantRewardReferralQuery = {},
): Promise<TenantRewardReferral[]> {
  const parameters = new URLSearchParams();
  if (query.status !== undefined) parameters.set("status", query.status);
  if (query.limit !== undefined) parameters.set("limit", String(query.limit));
  if (query.offset !== undefined) parameters.set("offset", String(query.offset));
  const suffix = parameters.size > 0 ? `?${parameters.toString()}` : "";
  const payload = await request(`/api/v1/tenant/rewards/referrals${suffix}`);
  return getItems(payload, "referrals")
    .map(normalizeTenantRewardReferral)
    .filter((referral): referral is TenantRewardReferral => referral !== null);
}

export async function createTenantRewardReferral(
  input: TenantRewardReferralInput,
): Promise<TenantRewardReferralAttribution> {
  const payload = await request("/api/v1/tenant/rewards/referrals", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const data = unwrap(payload);
  const referral = isRecord(data) && isRecord(data.referral) ? data.referral : data;
  if (!isRecord(referral)) {
    throw new ApiError("The referral was recorded, but its response could not be read.", 500);
  }
  const publicId = asString(pick(referral, "publicId", "public_id"));
  if (!publicId)
    throw new ApiError("The referral was recorded, but its identifier was missing.", 500);
  return {
    publicId,
    status: normalizeTenantRewardReferralStatus(pick(referral, "status")),
    minimumSpendMinor: asNumber(pick(referral, "minimumSpendMinor", "minimum_spend_minor")),
    currencyCode: asString(pick(referral, "currencyCode", "currency_code"), "NPR"),
    createdAt: nullableString(pick(referral, "createdAt", "created_at")),
  };
}

export async function fetchTenantCustomers(
  query: TenantCustomerSearchQuery = {},
): Promise<TenantCustomerSummary[]> {
  const parameters = new URLSearchParams();
  if (query.q?.trim()) parameters.set("q", query.q.trim());
  if (query.limit !== undefined) parameters.set("limit", String(query.limit));
  if (query.offset !== undefined) parameters.set("offset", String(query.offset));
  const suffix = parameters.size > 0 ? `?${parameters.toString()}` : "";
  const payload = await request(`/api/v1/customers${suffix}`);
  return getItems(payload, "customers")
    .map(normalizeTenantCustomerSummary)
    .filter((customer): customer is TenantCustomerSummary => customer !== null);
}

export async function issueTenantReward(
  rewardPublicId: string,
  input: TenantManualRewardIssueInput,
): Promise<TenantManualRewardIssueResult> {
  const payload = await request(
    `/api/v1/tenant/rewards/${encodeURIComponent(rewardPublicId)}/issue`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
  const data = unwrap(payload);
  if (!isRecord(data)) {
    throw new ApiError("The reward was issued, but its response could not be read.", 500);
  }
  const issuance = normalizeTenantRewardIssuance(data.issuance);
  if (!issuance) {
    throw new ApiError("The reward was issued, but its issuance details were incomplete.", 500);
  }
  return {
    issuance,
    token: nullableString(data.token),
    replayed: Boolean(data.replayed),
  };
}

export async function fetchTenantRewardLocations(): Promise<CheckoutLocation[]> {
  const payload = await request("/api/v1/tenant/rewards/locations");
  return getItems(payload, "locations")
    .filter(isRecord)
    .map((location) => ({
      publicId: asString(pick(location, "publicId", "public_id")),
      name: asString(pick(location, "name")),
      code: asString(pick(location, "code")),
      city: nullableString(pick(location, "city")),
      timezone: asString(pick(location, "timezone")),
    }))
    .filter((location) => Boolean(location.publicId && location.name));
}

function normalizeTenantRewardPointsProgram(value: unknown): TenantRewardPointsProgram | null {
  if (!isRecord(value)) return null;
  const program = isRecord(value.program) ? value.program : {};
  const publicId = asString(pick(value, "publicId", "public_id"));
  const programPublicId = asString(pick(program, "publicId", "public_id"));
  const programName = asString(pick(program, "name"));
  if (!publicId || !programPublicId || !programName) return null;
  return {
    publicId,
    program: { publicId: programPublicId, name: programName },
    currencyCode: asString(pick(value, "currencyCode", "currency_code"), "NPR"),
    versionNumber: asNumber(pick(value, "versionNumber", "version_number")),
    spendUnitMinor: asNumber(pick(value, "spendUnitMinor", "spend_unit_minor")),
    pointsPerUnit: asNumber(pick(value, "pointsPerUnit", "points_per_unit")),
    minimumQualifyingMinor: asNumber(
      pick(value, "minimumQualifyingMinor", "minimum_qualifying_minor"),
    ),
    maximumPointsPerTransaction: nullableNumber(
      pick(value, "maximumPointsPerTransaction", "maximum_points_per_transaction"),
    ),
    pointExpirationDays: nullableNumber(
      pick(value, "pointExpirationDays", "point_expiration_days"),
    ),
    status: asString(pick(value, "status"), "active") as TenantRewardPointsProgram["status"],
    effectiveFrom: asString(pick(value, "effectiveFrom", "effective_from")),
    effectiveTo: nullableString(pick(value, "effectiveTo", "effective_to")),
    createdAt: asString(pick(value, "createdAt", "created_at")),
  };
}

function normalizeBusinessRedemptionRule(value: unknown): BusinessRedemptionRule | null {
  if (!isRecord(value)) return null;
  const publicId = asString(pick(value, "publicId", "public_id"));
  const programPublicId = asString(pick(value, "programPublicId", "program_public_id"));
  if (!publicId || !programPublicId) return null;
  return {
    publicId,
    programPublicId,
    programName: asString(pick(value, "programName", "program_name")),
    currencyCode: asString(pick(value, "currencyCode", "currency_code"), "NPR"),
    versionNumber: asNumber(pick(value, "versionNumber", "version_number")),
    pointsPerUnit: asNumber(pick(value, "pointsPerUnit", "points_per_unit")),
    valuePerUnitMinor: asNumber(pick(value, "valuePerUnitMinor", "value_per_unit_minor")),
    minimumRedemptionPoints: asNumber(
      pick(value, "minimumRedemptionPoints", "minimum_redemption_points"),
    ),
    maximumRedemptionPointsPerTransaction: nullableNumber(
      pick(
        value,
        "maximumRedemptionPointsPerTransaction",
        "maximum_redemption_points_per_transaction",
      ),
    ),
    status: asString(pick(value, "status"), "active") as BusinessRedemptionRule["status"],
    effectiveFrom: asString(pick(value, "effectiveFrom", "effective_from")),
    effectiveTo: nullableString(pick(value, "effectiveTo", "effective_to")),
    createdAt: asString(pick(value, "createdAt", "created_at")),
  };
}

export async function fetchTenantRewardPointsProgram(): Promise<TenantRewardPointsProgram> {
  const data = dataRecord(await request("/api/v1/tenant/rewards/points-program"));
  const pointsProgram = normalizeTenantRewardPointsProgram(data.pointsProgram);
  if (!pointsProgram) throw new ApiError("The points program response was incomplete.", 500);
  return pointsProgram;
}

export async function fetchEditableTenantRewardPointsProgram(): Promise<TenantRewardPointsProgram | null> {
  const data = dataRecord(await request("/api/v1/tenant/rewards/points-program/editable"));
  if (data.pointsProgram === null || data.pointsProgram === undefined) return null;
  const pointsProgram = normalizeTenantRewardPointsProgram(data.pointsProgram);
  if (!pointsProgram) {
    throw new ApiError("The editable points program response was incomplete.", 500);
  }
  return pointsProgram;
}

export async function fetchTenantRewardPointRules(): Promise<TenantRewardPointsProgram[]> {
  const payload = await request("/api/v1/tenant/rewards/points-program/rules");
  return getItems(payload, "rules")
    .map(normalizeTenantRewardPointsProgram)
    .filter((rule): rule is TenantRewardPointsProgram => rule !== null);
}

export async function updateTenantRewardPointsProgram(
  input: TenantRewardPointsProgramUpdateInput,
): Promise<TenantRewardPointsProgram> {
  const data = dataRecord(
    await request("/api/v1/tenant/rewards/points-program", {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  );
  const pointsProgram = normalizeTenantRewardPointsProgram(data.pointsProgram);
  if (!pointsProgram)
    throw new ApiError("The updated points program response was incomplete.", 500);
  return pointsProgram;
}

export async function saveTenantRewardPointsProgramDraft(
  input: TenantRewardPointsProgramUpdateInput,
): Promise<TenantRewardPointsProgram> {
  const data = dataRecord(
    await request("/api/v1/tenant/rewards/points-program/draft", {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  );
  const pointsProgram = normalizeTenantRewardPointsProgram(data.pointsProgram);
  if (!pointsProgram)
    throw new ApiError("The earning-rule draft response was incomplete.", 500);
  return pointsProgram;
}

export async function publishTenantRewardPointsProgramDraft(
  publicId: string,
): Promise<TenantRewardPointsProgram> {
  const data = dataRecord(
    await request(
      `/api/v1/tenant/rewards/points-program/${encodeURIComponent(publicId)}/publish`,
      { method: "POST" },
    ),
  );
  const pointsProgram = normalizeTenantRewardPointsProgram(data.pointsProgram);
  if (!pointsProgram)
    throw new ApiError("The published earning-rule response was incomplete.", 500);
  return pointsProgram;
}

export async function fetchBusinessRedemptionRules(): Promise<BusinessRedemptionRule[]> {
  const payload = await request("/api/v1/tenant/rewards/redemption-rules");
  return getItems(payload, "rules")
    .map(normalizeBusinessRedemptionRule)
    .filter((rule): rule is BusinessRedemptionRule => rule !== null);
}

export async function updateBusinessRedemptionRule(
  input: BusinessRedemptionRuleInput,
): Promise<BusinessRedemptionRule> {
  const data = dataRecord(
    await request("/api/v1/tenant/rewards/redemption-rules", {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  );
  const rule = normalizeBusinessRedemptionRule(data.rule);
  if (!rule) throw new ApiError("The updated redemption rule response was incomplete.", 500);
  return rule;
}

export async function fetchTenantRewardAutomations(): Promise<TenantRewardAutomation[]> {
  const payload = await request("/api/v1/tenant/rewards/automations");
  return getItems(payload, "automations")
    .map(normalizeTenantRewardAutomation)
    .filter((automation): automation is TenantRewardAutomation => automation !== null);
}

export async function createTenantRewardAutomation(
  input: TenantRewardAutomationInput,
): Promise<TenantRewardAutomation> {
  const payload = await request("/api/v1/tenant/rewards/automations", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const data = unwrap(payload);
  const automation = normalizeTenantRewardAutomation(
    isRecord(data) ? (data.automation ?? data) : data,
    0,
  );
  if (!automation)
    throw new ApiError("The automation was created, but its details could not be read.", 500);
  return automation;
}

export async function updateTenantRewardAutomation(
  publicId: string,
  input: TenantRewardAutomationUpdateInput,
): Promise<TenantRewardAutomation> {
  const payload = await request(
    `/api/v1/tenant/rewards/automations/${encodeURIComponent(publicId)}`,
    {
      method: "PATCH",
      body: JSON.stringify(input),
    },
  );
  const data = unwrap(payload);
  const automation = normalizeTenantRewardAutomation(
    isRecord(data) ? (data.automation ?? data) : data,
    0,
  );
  if (!automation)
    throw new ApiError("The automation was updated, but its details could not be read.", 500);
  return automation;
}

export async function updateTenantRewardAutomationStatus(
  publicId: string,
  status: TenantRewardAutomationStatus,
): Promise<TenantRewardAutomation> {
  const payload = await request(
    `/api/v1/tenant/rewards/automations/${encodeURIComponent(publicId)}/status`,
    {
      method: "PUT",
      body: JSON.stringify({ status }),
    },
  );
  const data = unwrap(payload);
  const automation = normalizeTenantRewardAutomation(
    isRecord(data) ? (data.automation ?? data) : data,
    0,
  );
  if (!automation)
    throw new ApiError("The automation status changed, but its details could not be read.", 500);
  return automation;
}

export async function fetchTenantRewardRedemptions(): Promise<TenantRewardRedemption[]> {
  const payload = await request("/api/v1/tenant/rewards/redemptions");
  return getItems(payload, "redemptions")
    .map(normalizeTenantRewardRedemption)
    .filter((redemption): redemption is TenantRewardRedemption => redemption !== null);
}

export async function validateTenantRewardRedemption(
  input: TenantRewardRedemptionCheckInput,
): Promise<TenantRewardRedemption> {
  const payload = await request("/api/v1/tenant/rewards/redemptions/validate", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const data = unwrap(payload);
  const redemption = normalizeTenantRewardRedemption(
    isRecord(data) ? (data.redemption ?? data) : data,
  );
  if (!redemption) throw new ApiError("The redemption response could not be read.", 500);
  return redemption;
}

export async function fulfillTenantRewardRedemption(
  publicId: string,
  input: TenantRewardFulfillmentInput,
): Promise<TenantRewardRedemption> {
  const payload = await request(
    `/api/v1/tenant/rewards/redemptions/${encodeURIComponent(publicId)}/fulfill`,
    {
      method: "POST",
      body: JSON.stringify(input),
    },
  );
  const data = unwrap(payload);
  const redemption = normalizeTenantRewardRedemption(
    isRecord(data) ? (data.redemption ?? data) : data,
  );
  if (!redemption) throw new ApiError("The fulfilled redemption response could not be read.", 500);
  return redemption;
}

export async function reverseTenantRewardRedemption(
  publicId: string,
  reason: string,
): Promise<TenantRewardRedemption> {
  const payload = await request(
    `/api/v1/tenant/rewards/redemptions/${encodeURIComponent(publicId)}/reverse`,
    {
      method: "POST",
      body: JSON.stringify({ reason }),
    },
  );
  const data = unwrap(payload);
  const redemption = normalizeTenantRewardRedemption(
    isRecord(data) ? (data.redemption ?? data) : data,
  );
  if (!redemption) throw new ApiError("The reversed redemption response could not be read.", 500);
  return redemption;
}

export async function fetchTenantRewardAudit(): Promise<TenantRewardAuditEvent[]> {
  const payload = await request("/api/v1/tenant/rewards/audit");
  return getItems(payload, "events")
    .map(normalizeTenantRewardAuditEvent)
    .filter((event): event is TenantRewardAuditEvent => event !== null);
}

async function tenantRewardAction(
  publicId: string,
  action: "submit" | "approve" | "reject" | "activate" | "pause" | "archive" | "duplicate",
  body?: Record<string, unknown>,
): Promise<Reward> {
  const payload = await request(
    `/api/v1/tenant/rewards/${encodeURIComponent(publicId)}/${action}`,
    {
      method: "POST",
      ...(body ? { body: JSON.stringify(body) } : {}),
    },
  );
  const data = unwrap(payload);
  const reward = normalizeReward(isRecord(data) ? (data.reward ?? data) : data, 0);
  if (!reward)
    throw new ApiError("The reward was updated, but its details could not be read.", 500);
  return reward;
}

export const submitTenantReward = (publicId: string) => tenantRewardAction(publicId, "submit");
export const approveTenantReward = (publicId: string) => tenantRewardAction(publicId, "approve");
export const rejectTenantReward = (publicId: string, reason: string) =>
  tenantRewardAction(publicId, "reject", { reason });
export const activateTenantReward = (publicId: string) => tenantRewardAction(publicId, "activate");
export const pauseTenantReward = (publicId: string) => tenantRewardAction(publicId, "pause");
export const archiveTenantReward = (publicId: string) => tenantRewardAction(publicId, "archive");
export const duplicateTenantReward = (publicId: string) =>
  tenantRewardAction(publicId, "duplicate");

export async function setTenantStatus(publicId: string, status: Tenant["status"]): Promise<void> {
  await request(`/api/v1/super-admin/tenants/${encodeURIComponent(publicId)}/status`, {
    method: "PATCH",
    body: JSON.stringify({ status }),
  });
}

export async function reviewTenantApplication(
  publicId: string,
  decision: "approve" | "reject",
  note: string | null = null,
): Promise<Tenant> {
  const data = dataRecord(
    await request(
      `/api/v1/super-admin/tenants/${encodeURIComponent(publicId)}/application-decision`,
      {
        method: "POST",
        body: JSON.stringify({ decision, note }),
      },
    ),
  );
  const tenant = normalizeTenant(data.tenant, 0);
  if (!tenant) throw new ApiError("The reviewed tenant could not be read.", 500);
  return tenant;
}

export async function fetchPlatformSummary(): Promise<PlatformSummary> {
  const data = dataRecord(await request("/api/v1/super-admin/summary"));
  return data.summary as PlatformSummary;
}

export async function fetchPlatformUsers(): Promise<PlatformUser[]> {
  const payload = await request("/api/v1/super-admin/users?limit=100");
  const data = dataRecord(payload);
  return Array.isArray(data.users) ? (data.users as PlatformUser[]) : [];
}

export async function updatePlatformUser(
  publicId: string,
  input: Partial<
    Pick<
      PlatformUser,
      | "firstName"
      | "lastName"
      | "email"
      | "phone"
      | "profileImageUrl"
      | "birthday"
      | "addressLine1"
      | "addressLine2"
      | "city"
      | "district"
      | "province"
      | "country"
      | "postalCode"
    >
  >,
): Promise<PlatformUser> {
  const data = dataRecord(
    await request(`/api/v1/super-admin/users/${encodeURIComponent(publicId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  );
  return data.user as PlatformUser;
}

export async function setPlatformUserStatus(
  publicId: string,
  status: PlatformUser["status"],
): Promise<PlatformUser> {
  const data = dataRecord(
    await request(`/api/v1/super-admin/users/${encodeURIComponent(publicId)}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),
  );
  return data.user as PlatformUser;
}

export async function sendPlatformUserPasswordReset(publicId: string) {
  return dataRecord(
    await request(`/api/v1/super-admin/users/${encodeURIComponent(publicId)}/password-reset`, {
      method: "POST",
    }),
  ) as { delivery: "sent" | "temporarily_unavailable"; email: string };
}

export async function archivePlatformUser(
  publicId: string,
  confirmation: string,
): Promise<ArchivedPlatformAccount> {
  const data = dataRecord(
    await request(`/api/v1/super-admin/users/${encodeURIComponent(publicId)}/archive`, {
      method: "POST",
      body: JSON.stringify({ confirmation }),
    }),
  );
  if (!isRecord(data.archive)) {
    throw new ApiError("The archived user result could not be read.", 500);
  }
  return data.archive as unknown as ArchivedPlatformAccount;
}

export async function fetchPlatformSettings(): Promise<PlatformSettings> {
  const data = dataRecord(await request("/api/v1/super-admin/settings"));
  return data.settings as PlatformSettings;
}

export async function updatePlatformSettings(input: PlatformSettings): Promise<PlatformSettings> {
  const data = dataRecord(
    await request("/api/v1/super-admin/settings", {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  );
  return data.settings as PlatformSettings;
}

function dataRecord(payload: unknown): Record<string, unknown> {
  const data = unwrap(payload);
  return isRecord(data) ? data : {};
}

export async function fetchTierStatus(): Promise<TierStatus> {
  const data = dataRecord(await request("/api/v1/me/tier"));
  if (!isRecord(data.tier)) throw new ApiError("Your tier status could not be read.", 500);
  return data.tier as unknown as TierStatus;
}

export async function fetchPublicTierProgram(): Promise<PublicTierProgram> {
  return dataRecord(await request("/api/v1/platform/tiers")) as unknown as PublicTierProgram;
}

export async function fetchTierActivity(): Promise<TierActivity[]> {
  const data = dataRecord(await request("/api/v1/me/tier/activity"));
  return Array.isArray(data.activity) ? (data.activity as TierActivity[]) : [];
}

export async function fetchMembershipQr(): Promise<MembershipQr> {
  const data = dataRecord(await request("/api/v1/me/membership-qr"));
  if (!isRecord(data.membershipQr)) throw new ApiError("Your member code could not be read.", 500);
  return data.membershipQr as unknown as MembershipQr;
}

export async function fetchCheckoutLocations(tenantSlug: string): Promise<CheckoutLocation[]> {
  const data = dataRecord(
    await request(`/api/v1/checkout/locations?tenantSlug=${encodeURIComponent(tenantSlug)}`),
  );
  return Array.isArray(data.locations) ? (data.locations as CheckoutLocation[]) : [];
}

export async function fetchCheckoutTransactions(): Promise<CheckoutTransaction[]> {
  const data = dataRecord(await request("/api/v1/checkout/transactions?limit=100&offset=0"));
  return Array.isArray(data.transactions) ? (data.transactions as CheckoutTransaction[]) : [];
}

export async function createCheckoutTransaction(input: {
  locationPublicId: string;
  customerCode: string;
  grossAmountMinor: number;
  paymentType: string;
  merchantReference?: string;
  rewardPointsToRedeem?: number;
  lineItems?: TenantRewardLineItemFact[];
}): Promise<CheckoutTransaction> {
  const data = dataRecord(
    await request("/api/v1/checkout/transactions", {
      method: "POST",
      body: JSON.stringify({ ...input, idempotencyKey: crypto.randomUUID() }),
    }),
  );
  if (!isRecord(data.transaction)) throw new ApiError("The checkout could not be read.", 500);
  return data.transaction as unknown as CheckoutTransaction;
}

export async function fetchCheckoutRedemptionQuote(input: {
  customerCode: string;
  locationPublicId: string;
  grossAmountMinor: number;
}): Promise<CheckoutRedemptionQuote> {
  const query = new URLSearchParams({
    customerCode: input.customerCode,
    locationPublicId: input.locationPublicId,
    grossAmountMinor: String(input.grossAmountMinor),
  });
  const data = dataRecord(await request(`/api/v1/checkout/redemption-quote?${query}`));
  if (!isRecord(data.quote))
    throw new ApiError("The Reward Point redemption quote could not be read.", 500);
  return data.quote as unknown as CheckoutRedemptionQuote;
}

async function checkoutAction(
  publicId: string,
  action: string,
  body: Record<string, unknown> = {},
) {
  const data = dataRecord(
    await request(`/api/v1/checkout/transactions/${encodeURIComponent(publicId)}/${action}`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  );
  if (!isRecord(data.transaction))
    throw new ApiError("The checkout update could not be read.", 500);
  return data.transaction as unknown as CheckoutTransaction;
}

export const confirmCheckout = (publicId: string) => checkoutAction(publicId, "confirm");
export const customerConfirmCheckout = (publicId: string) =>
  checkoutAction(publicId, "customer-confirm");
export const approveCheckout = (publicId: string) => checkoutAction(publicId, "manager-approve");
export const rejectCheckout = (publicId: string, reason: string) =>
  checkoutAction(publicId, "reject", { reason });
export const reverseCheckout = (publicId: string, reason: string) =>
  checkoutAction(publicId, "reverse", {
    reason,
    idempotencyKey: crypto.randomUUID(),
  });

export async function issueCheckoutClaim(input: {
  locationPublicId: string;
  grossAmountMinor: number;
  paymentType: string;
}) {
  const data = dataRecord(
    await request("/api/v1/checkout/claims", {
      method: "POST",
      body: JSON.stringify({ ...input, idempotencyKey: crypto.randomUUID() }),
    }),
  );
  if (!isRecord(data.claim)) throw new ApiError("The checkout code could not be read.", 500);
  return data.claim as unknown as {
    publicId: string;
    displayCode: string;
    claimToken: string;
    expiresAt: string;
    amount: { grossAmountMinor: number; currencyCode: string };
  };
}

export async function redeemCheckoutClaim(code: string): Promise<CheckoutTransaction> {
  const data = dataRecord(
    await request("/api/v1/checkout/claims/redeem", {
      method: "POST",
      body: JSON.stringify({ code, idempotencyKey: crypto.randomUUID() }),
    }),
  );
  if (!isRecord(data.transaction))
    throw new ApiError("The claimed checkout could not be read.", 500);
  return data.transaction as unknown as CheckoutTransaction;
}

export async function fetchReceiptSubmissions(): Promise<ReceiptSubmission[]> {
  const data = dataRecord(await request("/api/v1/checkout/receipts"));
  return Array.isArray(data.receipts) ? (data.receipts as ReceiptSubmission[]) : [];
}

export async function submitReceipt(input: {
  tenantSlug: string;
  locationPublicId?: string;
  grossAmountMinor: number;
  merchantReference?: string;
  receipt?: File;
}): Promise<ReceiptSubmission> {
  const form = new FormData();
  form.set("tenantSlug", input.tenantSlug);
  if (input.locationPublicId) form.set("locationPublicId", input.locationPublicId);
  form.set("grossAmountMinor", String(input.grossAmountMinor));
  if (input.merchantReference) form.set("merchantReference", input.merchantReference);
  if (input.receipt) form.set("receipt", input.receipt);
  const data = dataRecord(
    await request("/api/v1/checkout/receipts", { method: "POST", body: form }),
  );
  if (!isRecord(data.receipt)) throw new ApiError("The receipt submission could not be read.", 500);
  return data.receipt as unknown as ReceiptSubmission;
}

export async function reviewReceipt(
  publicId: string,
  action: "approved" | "rejected",
  reason?: string,
) {
  const data = dataRecord(
    await request(`/api/v1/checkout/receipts/${encodeURIComponent(publicId)}/review`, {
      method: "POST",
      body: JSON.stringify({ action, reason, idempotencyKey: crypto.randomUUID() }),
    }),
  );
  return data.receipt as ReceiptSubmission;
}

export async function fetchCheckoutConfiguration(): Promise<CheckoutConfiguration> {
  return dataRecord(await request("/api/v1/checkout/settings")) as unknown as CheckoutConfiguration;
}

export async function updateCheckoutConfiguration(input: {
  locationPublicId?: string | null;
  customerConfirmationThresholdMinor: number;
  managerApprovalThresholdMinor: number;
  maximumTransactionMinor: number;
  maximumTransactionsPerCustomerDay: number;
  claimCodeTtlSeconds: number;
  receiptRecoveryWindowDays: number;
  offlineModeEnabled: boolean;
}): Promise<CheckoutConfiguration> {
  return dataRecord(
    await request("/api/v1/checkout/settings", {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  ) as unknown as CheckoutConfiguration;
}

export async function fetchCheckoutAnalytics(): Promise<CheckoutAnalytics> {
  return dataRecord(await request("/api/v1/checkout/analytics")) as unknown as CheckoutAnalytics;
}

export async function fetchTierAdminProgram(): Promise<TierAdminProgram> {
  return dataRecord(
    await request("/api/v1/platform-admin/tier-program"),
  ) as unknown as TierAdminProgram;
}

export interface TierRuleInput {
  spendUnitMinor: number;
  tierPointsPerUnit: number;
  minimumQualifyingMinor: number;
  maximumTierPointsPerTransaction: number | null;
  effectiveFrom: string;
}

export async function createTierRule(input: TierRuleInput) {
  const data = dataRecord(
    await request("/api/v1/platform-admin/tier-rules", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  );
  return data.rule;
}

export async function publishTierRule(publicId: string) {
  const data = dataRecord(
    await request(`/api/v1/platform-admin/tier-rules/${encodeURIComponent(publicId)}/publish`, {
      method: "POST",
    }),
  );
  return data.rule;
}

export async function updateTierRule(publicId: string, input: TierRuleInput) {
  const data = dataRecord(
    await request(`/api/v1/platform-admin/tier-rules/${encodeURIComponent(publicId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  );
  return data.rule as TierAdminRule;
}

export async function createTierRuleDraft(publicId: string): Promise<TierAdminRule> {
  const data = dataRecord(
    await request(`/api/v1/platform-admin/tier-rules/${encodeURIComponent(publicId)}/draft`, {
      method: "POST",
    }),
  );
  return data.rule as TierAdminRule;
}

export async function archiveTierRule(publicId: string): Promise<TierAdminRule> {
  const data = dataRecord(
    await request(`/api/v1/platform-admin/tier-rules/${encodeURIComponent(publicId)}/archive`, {
      method: "POST",
    }),
  );
  return data.rule as TierAdminRule;
}

export async function deleteTierRule(publicId: string): Promise<void> {
  await request(`/api/v1/platform-admin/tier-rules/${encodeURIComponent(publicId)}`, {
    method: "DELETE",
  });
}

export async function runTierAssessment() {
  const data = dataRecord(
    await request("/api/v1/platform-admin/tier-assessments", {
      method: "POST",
      body: JSON.stringify({}),
    }),
  );
  return data.assessment;
}

export function messageFromError(error: unknown) {
  return error instanceof Error ? error.message : "Something went wrong. Please try again.";
}

export interface MeroCreditConfiguration {
  regulatoryState: "BLOCKED" | "APPROVED";
  purchasingEnabled?: boolean;
  customerPurchasingEnabled?: boolean;
  businessPurchasingEnabled?: boolean;
  purchaseEnabled?: boolean;
  globalRedemptionEnabled: boolean;
  minimumPurchaseMinor: number;
  maximumPurchaseMinor: number;
  dailyPurchaseLimitMinor?: number;
  monthlyPurchaseLimitMinor?: number;
  allowedDenominationsMinor: number[];
  customAmountEnabled: boolean;
  approvedProviders: string[];
  refundMode: "DISABLED" | "UNUSED_ONLY" | "PARTIAL_CONTROLLED";
  purchasedExpiryDays: number | null;
  promotionalDefaultExpiryDays?: number;
  adjustmentDualApprovalThresholdMinor?: number;
  regulatoryApprovalReference?: string | null;
  updatedAt?: string;
}

export interface MeroCreditActivity {
  publicId: string;
  entryType: string;
  amountMinor: number;
  currencyCode: "NPR";
  fundingBucket: string;
  previousBalanceMinor: number;
  newBalanceMinor: number;
  expiresAt: string | null;
  createdAt: string;
  ruleCode: string | null;
  merchantName: string | null;
}

export interface MeroCreditWalletData {
  wallet: {
    publicId: string;
    holderType: "CUSTOMER" | "BUSINESS";
    holderPublicId: string;
    holderName: string;
    currencyCode: "NPR";
    totalBalanceMinor: number;
    purchasedBalanceMinor: number;
    promotionalBalanceMinor: number;
    status: string;
    updatedAt: string;
  };
  breakdown: {
    purchasedMinor: number;
    promotionalMinor: number;
    expiringSoonMinor: number;
    nextExpiryAt: string | null;
  };
  activity: MeroCreditActivity[];
  configuration: MeroCreditConfiguration;
  offers: Array<Record<string, unknown>>;
  acceptedMerchants: Array<{
    publicId: string;
    slug: string;
    businessName: string;
    logoUrl: string | null;
    minimumBasketMinor: number;
    maximumPaymentBasisPoints: number;
    minimumRedemptionMinor: number;
    maximumRedemptionMinor: number;
  }>;
  separationNotice?: string;
  acceptance?: { enabled: boolean; minimumBasketMinor: number; maximumPaymentBasisPoints: number };
  settlements?: MeroCreditSettlement[];
  permittedUses?: string[];
  prohibitedUses?: string[];
}

export interface MeroCreditMerchantAcceptance {
  enabled: boolean;
  minimumBasketMinor: number;
  maximumPaymentBasisPoints: number;
  minimumRedemptionMinor: number;
  maximumRedemptionMinor: number;
  excludedCategoryCodes: string[];
  eligibleLocationPublicIds: string[];
  updatedAt: string | null;
}

export interface MeroCreditMerchantAcceptanceWorkspace {
  tenant: { publicId: string; slug: string; businessName: string };
  acceptance: MeroCreditMerchantAcceptance;
  locations: Array<{
    publicId: string;
    name: string;
    code: string;
    city: string | null;
    status: string;
  }>;
}

export type MeroCreditMerchantAcceptanceInput = Omit<MeroCreditMerchantAcceptance, "updatedAt">;

export interface MeroCreditRule {
  publicId: string;
  code: string;
  name: string;
  description: string;
  audience: string;
  triggerType: string;
  status: string;
  versionNumber: number;
  versionStatus: string;
  awardMinor: number;
  fundingBucket: string;
  eligibility: Record<string, unknown>;
  frequencyType: string;
  maximumAwardsPerHolder: number | null;
  totalBudgetMinor: number | null;
  consumedBudgetMinor: number;
  expiryMode: "NEVER" | "DAYS" | "FIXED_DATE";
  expiryDays: number | null;
  expiresAt: string | null;
  stackingMode: "STACKABLE" | "EXCLUSIVE" | "PRIORITY";
  priority: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  changeReason: string;
  activeVersionNumber: number | null;
  activeEffectiveFrom: string | null;
  activeEffectiveTo: string | null;
}

export interface MeroCreditRuleInput {
  code: string;
  name: string;
  description: string;
  audience: "CUSTOMER" | "BUSINESS" | "BOTH";
  triggerType:
    | "VERIFIED_FIRST_SIGNUP"
    | "TENANT_APPROVED"
    | "FIRST_QUALIFIED_PURCHASE"
    | "TIER_PROMOTED"
    | "REFERRAL_QUALIFIED"
    | "BIRTHDAY"
    | "MEMBERSHIP_ANNIVERSARY"
    | "TRANSACTION_MILESTONE"
    | "CROSS_BUSINESS_CHALLENGE"
    | "CAMPAIGN"
    | "ADMIN_EVENT";
  awardMinor: number;
  fundingBucket:
    "PROMOTIONAL" | "TIER_REWARD" | "CAMPAIGN" | "BUSINESS_INCENTIVE" | "ADMIN_ADJUSTMENT";
  eligibility: Record<string, unknown>;
  frequencyType: "ONCE_LIFETIME" | "ONCE_PER_EVENT" | "DAILY" | "MONTHLY" | "LIFETIME_CAPPED";
  maximumAwardsPerHolder: number | null;
  totalBudgetMinor: number | null;
  expiryMode: "NEVER" | "DAYS" | "FIXED_DATE";
  expiryDays: number | null;
  expiresAt: string | null;
  stackingMode: "STACKABLE" | "EXCLUSIVE" | "PRIORITY";
  priority: number;
  effectiveFrom: string;
  effectiveTo: string | null;
  changeReason: string;
}

export interface MeroCreditSettlement {
  publicId: string;
  tenantName: string;
  tenantPublicId: string;
  redemptionPublicId: string;
  grossAmountMinor: number;
  feeAmountMinor: number;
  netAmountMinor: number;
  status: string;
  createdAt: string;
  paidAt: string | null;
}

export async function fetchCustomerMeroCredits(): Promise<MeroCreditWalletData> {
  return dataRecord(
    await request("/api/v1/me/mero-credits/wallet"),
  ) as unknown as MeroCreditWalletData;
}

export async function fetchBusinessMeroCredits(): Promise<MeroCreditWalletData> {
  return dataRecord(
    await request("/api/v1/admin/mero-credits/wallet"),
  ) as unknown as MeroCreditWalletData;
}

export async function createMeroCreditPurchase(
  audience: "customer" | "business",
  input: { amountMinor: number; providerCode: string; purpose?: string },
) {
  const root = audience === "customer" ? "/api/v1/me" : "/api/v1/admin";
  return dataRecord(
    await request(`${root}/mero-credits/purchases`, {
      method: "POST",
      body: JSON.stringify({ ...input, idempotencyKey: crypto.randomUUID() }),
    }),
  );
}

export async function fetchMeroCreditDashboard() {
  return dataRecord(await request("/api/v1/platform/mero-credits/dashboard")) as unknown as {
    wallets: {
      walletCount: number;
      outstandingMinor: number;
      purchasedOutstandingMinor: number;
      promotionalOutstandingMinor: number;
      customerWallets: number;
      businessWallets: number;
    };
    ledger: {
      issuedMinor: number;
      redeemedMinor: number;
      expiredMinor: number;
      purchasedMinor: number;
    };
    settlements: { payableMinor: number; paidMinor: number; settlementCount: number };
    rules: { ruleCount: number; activeRules: number };
    risks: { openRiskEvents: number };
    recentActivity: Array<{
      publicId: string;
      entryType: string;
      amountMinor: number;
      createdAt: string;
      holderType: string;
      holderName: string;
    }>;
  };
}

export async function fetchMeroCreditConfiguration(): Promise<MeroCreditConfiguration> {
  const data = dataRecord(await request("/api/v1/platform/mero-credits/configuration"));
  return data.configuration as MeroCreditConfiguration;
}

export async function fetchMeroCreditMerchantAcceptance(
  tenantPublicId: string,
): Promise<MeroCreditMerchantAcceptanceWorkspace> {
  return dataRecord(
    await request(
      `/api/v1/platform/mero-credits/merchants/${encodeURIComponent(tenantPublicId)}/acceptance`,
    ),
  ) as unknown as MeroCreditMerchantAcceptanceWorkspace;
}

export async function updateMeroCreditMerchantAcceptance(
  tenantPublicId: string,
  input: MeroCreditMerchantAcceptanceInput,
): Promise<MeroCreditMerchantAcceptance> {
  const data = dataRecord(
    await request(
      `/api/v1/platform/mero-credits/merchants/${encodeURIComponent(tenantPublicId)}/acceptance`,
      {
        method: "PUT",
        body: JSON.stringify(input),
      },
    ),
  );
  return data.acceptance as MeroCreditMerchantAcceptance;
}

export async function updateMeroCreditConfiguration(
  input: Required<
    Pick<
      MeroCreditConfiguration,
      | "regulatoryState"
      | "purchasingEnabled"
      | "customerPurchasingEnabled"
      | "businessPurchasingEnabled"
      | "globalRedemptionEnabled"
      | "minimumPurchaseMinor"
      | "maximumPurchaseMinor"
      | "dailyPurchaseLimitMinor"
      | "monthlyPurchaseLimitMinor"
      | "allowedDenominationsMinor"
      | "customAmountEnabled"
      | "approvedProviders"
      | "refundMode"
      | "purchasedExpiryDays"
      | "promotionalDefaultExpiryDays"
      | "adjustmentDualApprovalThresholdMinor"
      | "regulatoryApprovalReference"
    >
  >,
): Promise<MeroCreditConfiguration> {
  const data = dataRecord(
    await request("/api/v1/platform/mero-credits/configuration", {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  );
  return data.configuration as MeroCreditConfiguration;
}

export async function fetchMeroCreditRules(): Promise<MeroCreditRule[]> {
  const data = dataRecord(await request("/api/v1/platform/mero-credits/rules"));
  return Array.isArray(data.rules) ? (data.rules as MeroCreditRule[]) : [];
}

export function serializeMeroCreditRuleInput(input: MeroCreditRuleInput): MeroCreditRuleInput {
  return {
    code: input.code,
    name: input.name,
    description: input.description,
    audience: input.audience,
    triggerType: input.triggerType,
    awardMinor: input.awardMinor,
    fundingBucket: input.fundingBucket,
    eligibility: input.eligibility,
    frequencyType: input.frequencyType,
    maximumAwardsPerHolder: input.maximumAwardsPerHolder,
    totalBudgetMinor: input.totalBudgetMinor,
    expiryMode: input.expiryMode,
    expiryDays: input.expiryDays,
    expiresAt: input.expiresAt,
    stackingMode: input.stackingMode,
    priority: input.priority,
    effectiveFrom: input.effectiveFrom,
    effectiveTo: input.effectiveTo,
    changeReason: input.changeReason,
  };
}

export async function createMeroCreditRule(input: {
  code: string;
  name: string;
  description: string;
  audience: "CUSTOMER" | "BUSINESS" | "BOTH";
  triggerType:
    | "VERIFIED_FIRST_SIGNUP"
    | "TENANT_APPROVED"
    | "FIRST_QUALIFIED_PURCHASE"
    | "TIER_PROMOTED"
    | "REFERRAL_QUALIFIED"
    | "BIRTHDAY"
    | "MEMBERSHIP_ANNIVERSARY"
    | "TRANSACTION_MILESTONE"
    | "CROSS_BUSINESS_CHALLENGE"
    | "CAMPAIGN"
    | "ADMIN_EVENT";
  awardMinor: number;
  fundingBucket:
    "PROMOTIONAL" | "TIER_REWARD" | "CAMPAIGN" | "BUSINESS_INCENTIVE" | "ADMIN_ADJUSTMENT";
  totalBudgetMinor: number | null;
  expiryDays: number;
}) {
  const data = dataRecord(
    await request("/api/v1/platform/mero-credits/rules", {
      method: "POST",
      body: JSON.stringify({
        ...input,
        eligibility: {},
        frequencyType: "ONCE_LIFETIME",
        maximumAwardsPerHolder: 1,
        expiryMode: "DAYS",
        expiresAt: null,
        stackingMode: "STACKABLE",
        priority: 100,
        effectiveFrom: new Date().toISOString(),
        effectiveTo: null,
        changeReason: "Created in the Platform Admin rule builder",
      }),
    }),
  );
  return data.rule as MeroCreditRule;
}

export async function createPlatformMeroCreditRule(input: MeroCreditRuleInput) {
  const data = dataRecord(
    await request("/api/v1/platform/mero-credits/rules", {
      method: "POST",
      body: JSON.stringify(serializeMeroCreditRuleInput(input)),
    }),
  );
  return data.rule as MeroCreditRule;
}

export async function activateMeroCreditRule(publicId: string) {
  const data = dataRecord(
    await request(`/api/v1/platform/mero-credits/rules/${encodeURIComponent(publicId)}/activate`, {
      method: "POST",
    }),
  );
  return data.rule as MeroCreditRule;
}

export async function pauseMeroCreditRule(publicId: string) {
  const data = dataRecord(
    await request(`/api/v1/platform/mero-credits/rules/${encodeURIComponent(publicId)}/pause`, {
      method: "POST",
    }),
  );
  return data.rule as MeroCreditRule;
}

export async function updateMeroCreditRule(publicId: string, input: MeroCreditRuleInput) {
  const data = dataRecord(
    await request(`/api/v1/platform/mero-credits/rules/${encodeURIComponent(publicId)}`, {
      method: "PATCH",
      body: JSON.stringify(serializeMeroCreditRuleInput(input)),
    }),
  );
  return data.rule as MeroCreditRule;
}

export async function createMeroCreditRuleDraft(
  publicId: string,
  input: Omit<MeroCreditRuleInput, "code" | "name" | "description" | "audience" | "triggerType">,
) {
  const data = dataRecord(
    await request(`/api/v1/platform/mero-credits/rules/${encodeURIComponent(publicId)}`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  );
  return data.rule as MeroCreditRule;
}

export async function archiveMeroCreditRule(publicId: string) {
  const data = dataRecord(
    await request(`/api/v1/platform/mero-credits/rules/${encodeURIComponent(publicId)}/archive`, {
      method: "POST",
    }),
  );
  return data.rule as MeroCreditRule;
}

export async function deleteMeroCreditRule(publicId: string): Promise<void> {
  await request(`/api/v1/platform/mero-credits/rules/${encodeURIComponent(publicId)}`, {
    method: "DELETE",
  });
}

export async function fetchMeroCreditSettlements(): Promise<MeroCreditSettlement[]> {
  const data = dataRecord(await request("/api/v1/platform/mero-credits/settlements"));
  return Array.isArray(data.settlements) ? (data.settlements as MeroCreditSettlement[]) : [];
}
